import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '../context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Apple, Droplets, Heart, Brain, AlertTriangle, CheckCircle,
  ChevronDown, ChevronUp, ChevronRight, ChevronLeft, Activity, Zap, Shield, Target, Pill, User,
  TrendingUp, TrendingDown, Minus, Loader2, X, UtensilsCrossed, Leaf,
  Sparkles, Flame, Microscope, Atom, Gauge, Menu, Scale, Ruler,
  Calendar, Weight, HeartPulse, Beaker, TestTube, FileText, Plus,
  Camera, Image, Upload, Trash2, ArrowLeft, Layers
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import { Textarea } from '../components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import FeatureNav from '../components/FeatureNav';
import PDFExportButton from '../components/PDFExportButton';
import EnhancedBodyMap from '../components/EnhancedBodyMap';
import GlobalPatientPanel from '../components/GlobalPatientPanel';
import { usePatient } from '../context/PatientContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Custom hook for swipe detection - with vertical tolerance to prevent scroll interference
const useSwipe = (onSwipeLeft, onSwipeRight, threshold = 80) => {
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const touchEndX = useRef(null);
  const touchEndY = useRef(null);
  
  const onTouchStart = (e) => {
    touchEndX.current = null;
    touchEndY.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
    touchStartY.current = e.targetTouches[0].clientY;
  };
  
  const onTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
    touchEndY.current = e.targetTouches[0].clientY;
  };
  
  const onTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const distanceX = touchStartX.current - touchEndX.current;
    const distanceY = touchStartY.current - touchEndY.current;
    
    // Only trigger swipe if horizontal movement is greater than vertical
    // This prevents swipe from triggering during vertical scroll
    const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY) * 1.5;
    
    if (!isHorizontalSwipe) return;
    
    const isLeftSwipe = distanceX > threshold;
    const isRightSwipe = distanceX < -threshold;
    
    if (isLeftSwipe && onSwipeLeft) onSwipeLeft();
    if (isRightSwipe && onSwipeRight) onSwipeRight();
  };
  
  return { onTouchStart, onTouchMove, onTouchEnd };
};

// Multi-scale analysis levels (mirrors disease dashboard depth)
const ANALYSIS_SCALES = [
  { id: 'overview', name: 'Overview', icon: '📊', description: 'Summary & health score' },
  { id: 'items', name: 'Food Items', icon: '🍽️', description: 'Individual food breakdown' },
  { id: 'nutrients', name: 'Nutrients', icon: '🧬', description: 'All nutrients breakdown' },
  { id: 'molecular', name: 'Molecular', icon: '⚗️', description: 'Molecular compounds' },
  { id: 'cellular', name: 'Cellular', icon: '🔬', description: 'Cell-level effects' },
  { id: 'physiology', name: 'Physiology', icon: '⚡', description: 'Organ functions' },
  { id: 'clinical', name: 'Clinical', icon: '🩺', description: 'Lab parameters' },
];

// Organ systems for parameter effects
const ORGAN_SYSTEMS = [
  { id: 'cardiovascular', name: 'Cardio', icon: '❤️', color: 'from-red-500 to-rose-600' },
  { id: 'renal', name: 'Renal', icon: '🫘', color: 'from-amber-500 to-orange-600' },
  { id: 'hepatic', name: 'Hepatic', icon: '🫀', color: 'from-purple-500 to-violet-600' },
  { id: 'glycemic', name: 'Glycemic', icon: '🍬', color: 'from-pink-500 to-rose-600' },
  { id: 'gastrointestinal', name: 'GI', icon: '🥗', color: 'from-green-500 to-emerald-600' },
  { id: 'neurological', name: 'Neuro', icon: '🧠', color: 'from-blue-500 to-cyan-600' },
  { id: 'musculoskeletal', name: 'MSK', icon: '💪', color: 'from-orange-500 to-yellow-600' },
  { id: 'immune', name: 'Immune', icon: '🛡️', color: 'from-teal-500 to-cyan-600' },
  { id: 'endocrine', name: 'Endocrine', icon: '⚖️', color: 'from-indigo-500 to-purple-600' },
  { id: 'hematologic', name: 'Blood', icon: '🩸', color: 'from-red-600 to-rose-700' },
];

// Health score explanation
const HEALTH_SCORE_EXPLANATION = {
  '90-100': { label: 'Excellent', color: 'text-green-500', desc: 'Highly nutritious, minimal concerns' },
  '70-89': { label: 'Good', color: 'text-lime-500', desc: 'Generally healthy with minor considerations' },
  '50-69': { label: 'Moderate', color: 'text-yellow-500', desc: 'Balance of benefits and concerns' },
  '30-49': { label: 'Caution', color: 'text-orange-500', desc: 'Significant concerns, consume mindfully' },
  '0-29': { label: 'Low', color: 'text-red-500', desc: 'Multiple health concerns, limit intake' },
};

const getScoreInfo = (score) => {
  if (score >= 90) return HEALTH_SCORE_EXPLANATION['90-100'];
  if (score >= 70) return HEALTH_SCORE_EXPLANATION['70-89'];
  if (score >= 50) return HEALTH_SCORE_EXPLANATION['50-69'];
  if (score >= 30) return HEALTH_SCORE_EXPLANATION['30-49'];
  return HEALTH_SCORE_EXPLANATION['0-29'];
};

// Food icons based on type
const getFoodIcon = (name) => {
  const n = (name || '').toLowerCase();
  if (n.includes('vitamin') || n.includes('supplement') || n.includes('omega')) return '💊';
  if (n.includes('juice') || n.includes('water') || n.includes('tea') || n.includes('coffee') || n.includes('cola') || n.includes('milk')) return '🥤';
  if (n.includes('chicken') || n.includes('beef') || n.includes('fish') || n.includes('salmon') || n.includes('egg') || n.includes('meat')) return '🥩';
  if (n.includes('rice') || n.includes('bread') || n.includes('pasta') || n.includes('oat') || n.includes('wheat')) return '🌾';
  if (n.includes('apple') || n.includes('banana') || n.includes('orange') || n.includes('mango') || n.includes('berry') || n.includes('fruit')) return '🍎';
  if (n.includes('spinach') || n.includes('broccoli') || n.includes('carrot') || n.includes('salad') || n.includes('vegetable')) return '🥬';
  if (n.includes('pizza') || n.includes('burger') || n.includes('curry') || n.includes('tikka') || n.includes('biryani')) return '🍽️';
  if (n.includes('cheese') || n.includes('yogurt') || n.includes('dairy')) return '🧀';
  return '🍴';
};

// Quick suggestions
const QUICK_FOODS = [
  'Banana', 'Salmon', 'Spinach', 'Chicken Tikka Masala', 'Green Tea',
  'Vitamin D', 'Avocado', 'Oatmeal', 'Eggs', 'Almonds', 'Coffee', 'Rice'
];

// Popular foods by category for the homepage
const POPULAR_FOODS_BY_CATEGORY = [
  {
    category: 'Heart Healthy',
    icon: '❤️',
    color: 'from-red-500 to-pink-500',
    bgLight: 'bg-red-50',
    bgDark: 'bg-red-900/20',
    foods: ['Salmon', 'Avocado', 'Walnuts', 'Olive Oil', 'Oatmeal']
  },
  {
    category: 'Brain Boosters',
    icon: '🧠',
    color: 'from-purple-500 to-indigo-500',
    bgLight: 'bg-purple-50',
    bgDark: 'bg-purple-900/20',
    foods: ['Blueberries', 'Dark Chocolate', 'Eggs', 'Turmeric', 'Broccoli']
  },
  {
    category: 'Energy Foods',
    icon: '⚡',
    color: 'from-yellow-500 to-orange-500',
    bgLight: 'bg-yellow-50',
    bgDark: 'bg-yellow-900/20',
    foods: ['Banana', 'Quinoa', 'Sweet Potato', 'Almonds', 'Coffee']
  },
  {
    category: 'Immunity Boosters',
    icon: '🛡️',
    color: 'from-green-500 to-emerald-500',
    bgLight: 'bg-green-50',
    bgDark: 'bg-green-900/20',
    foods: ['Citrus Fruits', 'Garlic', 'Ginger', 'Spinach', 'Green Tea']
  },
  {
    category: 'Gut Health',
    icon: '🦠',
    color: 'from-teal-500 to-cyan-500',
    bgLight: 'bg-teal-50',
    bgDark: 'bg-teal-900/20',
    foods: ['Yogurt', 'Kimchi', 'Sauerkraut', 'Kefir', 'Miso']
  },
  {
    category: 'Protein Rich',
    icon: '💪',
    color: 'from-blue-500 to-indigo-500',
    bgLight: 'bg-blue-50',
    bgDark: 'bg-blue-900/20',
    foods: ['Chicken Breast', 'Eggs', 'Lentils', 'Greek Yogurt', 'Tofu']
  }
];

// Fallback diseases list (used if API fails)
const FALLBACK_DISEASES = [
  'Diabetes Type 2', 'Diabetes Type 1', 'Hypertension', 'Heart Failure',
  'Coronary Artery Disease', 'Atrial Fibrillation', 'Chronic Kidney Disease',
  'Acute Kidney Injury', 'Liver Cirrhosis', 'NAFLD', 'Hepatitis B', 'Hepatitis C',
  'Gout', 'Hyperuricemia', 'Hyperlipidemia', 'Hypercholesterolemia',
  'Obesity', 'Metabolic Syndrome', 'Hypothyroidism', 'Hyperthyroidism',
  'Anemia', 'Iron Deficiency Anemia', 'B12 Deficiency', 'Folate Deficiency',
  'Osteoporosis', 'Osteoarthritis', 'Rheumatoid Arthritis',
  'COPD', 'Asthma', 'Pneumonia', 'Sleep Apnea',
  'Depression', 'Anxiety', 'Migraine', 'Epilepsy', 'Parkinson\'s Disease',
  'Celiac Disease', 'IBS', 'Crohn\'s Disease', 'Ulcerative Colitis', 'GERD',
  'Hypokalemia', 'Hyperkalemia', 'Hyponatremia', 'Hypernatremia',
  'Hypocalcemia', 'Hypercalcemia', 'Hypomagnesemia',
  'Stroke', 'TIA', 'DVT', 'Pulmonary Embolism',
  'Breast Cancer', 'Lung Cancer', 'Colon Cancer', 'Prostate Cancer',
  'HIV/AIDS', 'Tuberculosis', 'COVID-19', 'Influenza'
];

// Common allergies
const COMMON_ALLERGIES = [
  'Peanuts', 'Tree Nuts', 'Milk/Dairy', 'Eggs', 'Fish', 'Shellfish',
  'Wheat/Gluten', 'Soy', 'Sesame', 'Mustard', 'Sulfites'
];

// Common medications
const COMMON_MEDICATIONS = [
  'Metformin', 'Lisinopril', 'Amlodipine', 'Atorvastatin', 'Omeprazole',
  'Metoprolol', 'Losartan', 'Gabapentin', 'Levothyroxine', 'Warfarin',
  'Aspirin', 'Furosemide', 'Spironolactone', 'Prednisone', 'Insulin'
];

// Dietary preferences
const DIETARY_PREFS = [
  { id: 'vegetarian', label: 'Vegetarian', icon: '🥬' },
  { id: 'vegan', label: 'Vegan', icon: '🌱' },
  { id: 'halal', label: 'Halal', icon: '☪️' },
  { id: 'kosher', label: 'Kosher', icon: '✡️' },
  { id: 'gluten_free', label: 'Gluten-Free', icon: '🌾' },
  { id: 'dairy_free', label: 'Dairy-Free', icon: '🥛' },
  { id: 'low_sodium', label: 'Low Sodium', icon: '🧂' },
  { id: 'low_carb', label: 'Low Carb', icon: '🍞' },
  { id: 'keto', label: 'Keto', icon: '🥑' },
  { id: 'paleo', label: 'Paleo', icon: '🦴' },
];

// Parameter Card Component - Shows key info at a glance, expandable for details
const ParameterCard = ({ param, isDark, compact = false }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!param) return null;
  
  const directionColor = param.direction === 'increase' ? 'text-red-500' :
                        param.direction === 'decrease' ? 'text-blue-500' : 'text-gray-400';
  const bgColor = param.direction === 'increase' ? (isDark ? 'bg-red-900/20 border-red-800/30' : 'bg-red-50 border-red-200') :
                  param.direction === 'decrease' ? (isDark ? 'bg-blue-900/20 border-blue-800/30' : 'bg-blue-50 border-blue-200') :
                  (isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200');
  
  // Format quantified change with sign and unit
  const formatQuantifiedChange = () => {
    // Hide if null, undefined, or zero (minimal effect)
    if (param.quantified_change === null || param.quantified_change === undefined || param.quantified_change === 0) return null;
    const sign = param.quantified_change > 0 ? '+' : '';
    return `${sign}${param.quantified_change} ${param.change_unit || ''}`.trim();
  };
  
  const quantifiedDisplay = formatQuantifiedChange();
  
  return (
    <div className={`rounded-lg border p-2 sm:p-3 ${bgColor}`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full text-left">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            {param.direction === 'increase' && <TrendingUp className={`w-3.5 h-3.5 flex-shrink-0 ${directionColor}`} />}
            {param.direction === 'decrease' && <TrendingDown className={`w-3.5 h-3.5 flex-shrink-0 ${directionColor}`} />}
            {param.direction === 'neutral' && <Minus className={`w-3.5 h-3.5 flex-shrink-0 ${directionColor}`} />}
            <span className={`text-xs sm:text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {param.parameter_name || param.parameter?.replace(/_/g, ' ')}
            </span>
          </div>
          {/* Quantified Change - Always visible and prominent */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {quantifiedDisplay ? (
              <span className={`text-sm sm:text-base font-bold ${
                param.quantified_change > 0 ? 'text-red-500' : 
                param.quantified_change < 0 ? 'text-blue-500' : 'text-gray-400'
              }`}>
                {quantifiedDisplay}
              </span>
            ) : (
              <Badge variant="outline" className="text-[8px] sm:text-[9px]">{param.magnitude || param.direction}</Badge>
            )}
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''} ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
          </div>
        </div>
        {/* Show nutrient source inline for quick reference */}
        {param.from_nutrient && !expanded && (
          <div className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            via {param.from_nutrient}
          </div>
        )}
      </button>
      
      {expanded && (
        <div className={`mt-2 pt-2 border-t text-xs space-y-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          {/* Reference Range Box - Mathematical Precision */}
          {param.reference?.normal_range && (
            <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className={`text-[10px] font-medium uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Reference Range
                </span>
                <span className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {param.reference.unit}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`font-mono text-sm font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                  {param.reference.normal_range}
                </span>
                {param.quantified_change && param.quantified_change !== 0 && (
                  <span className={`text-xs ${param.quantified_change > 0 ? 'text-red-400' : 'text-blue-400'}`}>
                    → {param.quantified_change > 0 ? '+' : ''}{param.quantified_change} {param.change_unit || param.reference.unit}
                  </span>
                )}
              </div>
              {/* Visual deviation indicator */}
              {param.reference.normal_low !== null && param.reference.normal_high !== null && (
                <div className="mt-2">
                  <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 to-emerald-500" 
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className="flex justify-between mt-0.5">
                    <span className={`text-[9px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {param.reference.normal_low}
                    </span>
                    <span className={`text-[9px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {param.reference.normal_high}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Mechanism and Source */}
          <div className={`space-y-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {param.mechanism && <p><strong className={isDark ? 'text-gray-300' : 'text-gray-700'}>Mechanism:</strong> {param.mechanism}</p>}
            {param.from_nutrient && (
              <p>
                <strong className={isDark ? 'text-gray-300' : 'text-gray-700'}>From:</strong> {param.from_nutrient}
                {param.amount && <span className="font-mono ml-1">({param.amount})</span>}
              </p>
            )}
            {/* Show contributing ingredients for recipe analysis */}
            {param.from_ingredients && param.from_ingredients.length > 0 && (
              <p>
                <strong className={isDark ? 'text-gray-300' : 'text-gray-700'}>Source Ingredients:</strong>{' '}
                <span className="text-blue-400">{param.from_ingredients.join(', ')}</span>
              </p>
            )}
            {param.percent_dv && param.percent_dv > 0 && (
              <p>
                <strong className={isDark ? 'text-gray-300' : 'text-gray-700'}>% Daily Value:</strong>{' '}
                <span className="font-mono">{param.percent_dv}%</span>
              </p>
            )}
            {param.clinical_significance && (
              <p className="text-yellow-500">
                <strong>Clinical Note:</strong> {param.clinical_significance}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Nutrient Bar
const NutrientBar = ({ label, value, unit, percent, color = 'bg-green-500', isDark }) => {
  if (value === undefined || value === null) return null;
  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center">
        <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{label}</span>
        <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {value}{unit} {percent !== undefined && <span className="text-[10px] text-gray-500">({percent}% DV)</span>}
        </span>
      </div>
      <div className={`h-1.5 rounded-full ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`}>
        <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(percent || value/20, 100)}%` }} />
      </div>
    </div>
  );
};

// Individual Food Item Card Component
const FoodItemCard = ({ item, isDark }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!item) return null;
  
  // Determine overall effect color
  const getEffectColor = (effect) => {
    if (effect === 'beneficial') return 'text-green-500';
    if (effect === 'caution') return 'text-orange-500';
    if (effect === 'harmful') return 'text-red-500';
    return 'text-gray-400';
  };
  
  const getBgColor = (effect) => {
    if (effect === 'beneficial') return isDark ? 'bg-green-900/20 border-green-800/30' : 'bg-green-50 border-green-200';
    if (effect === 'caution') return isDark ? 'bg-orange-900/20 border-orange-800/30' : 'bg-orange-50 border-orange-200';
    if (effect === 'harmful') return isDark ? 'bg-red-900/20 border-red-800/30' : 'bg-red-50 border-red-200';
    return isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200';
  };
  
  // Count effects by type
  const effects = item.parameter_effects || {};
  const beneficialCount = Object.values(effects).filter(e => e?.effect === 'beneficial').length;
  const cautionCount = Object.values(effects).filter(e => e?.effect === 'caution').length;
  
  return (
    <div className={`rounded-lg border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
      <button 
        onClick={() => setExpanded(!expanded)} 
        className="w-full p-3 text-left"
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-lg">{item.item_name?.toLowerCase().includes('chicken') ? '🍗' : 
                                          item.item_name?.toLowerCase().includes('rice') ? '🍚' :
                                          item.item_name?.toLowerCase().includes('salad') ? '🥗' :
                                          item.item_name?.toLowerCase().includes('egg') ? '🥚' :
                                          item.item_name?.toLowerCase().includes('fish') ? '🐟' :
                                          item.item_name?.toLowerCase().includes('bread') ? '🍞' :
                                          item.item_name?.toLowerCase().includes('fruit') ? '🍎' :
                                          item.item_name?.toLowerCase().includes('vegetable') ? '🥦' : '🍽️'}</span>
              <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.item_name}</span>
            </div>
            <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              {item.estimated_portion} • {item.calories} kcal
            </div>
          </div>
          <div className="flex items-center gap-2">
            {beneficialCount > 0 && (
              <span className="text-xs text-green-500">✓{beneficialCount}</span>
            )}
            {cautionCount > 0 && (
              <span className="text-xs text-orange-500">⚠{cautionCount}</span>
            )}
            <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''} ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
          </div>
        </div>
        
        {/* Macros mini display */}
        <div className="flex gap-3 mt-2">
          <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            P: <span className="text-red-400">{item.macros?.protein_g}g</span>
          </span>
          <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            C: <span className="text-yellow-400">{item.macros?.carbs_g}g</span>
          </span>
          <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            F: <span className="text-blue-400">{item.macros?.fat_g}g</span>
          </span>
        </div>
      </button>
      
      {expanded && (
        <div className={`px-3 pb-3 space-y-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          {/* Key Nutrients */}
          {item.key_nutrients?.length > 0 && (
            <div className="pt-3">
              <span className={`text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Key Nutrients</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {item.key_nutrients.map((n, i) => (
                  <Badge key={i} variant="outline" className="text-[9px]">{n}</Badge>
                ))}
              </div>
            </div>
          )}
          
          {/* Parameter Effects */}
          {Object.keys(effects).length > 0 && (
            <div>
              <span className={`text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Body System Effects</span>
              <div className="grid gap-1.5 mt-1">
                {Object.entries(effects).map(([system, data]) => (
                  <div key={system} className={`p-2 rounded text-xs ${getBgColor(data.effect)}`}>
                    <div className="flex items-center justify-between">
                      <span className={`capitalize font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{system}</span>
                      <span className={`font-bold ${getEffectColor(data.effect)}`}>
                        {data.effect === 'beneficial' ? '✓ Good' : data.effect === 'caution' ? '⚠ Caution' : data.effect}
                      </span>
                    </div>
                    <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{data.reason}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Recommendation */}
          {item.recommendation && (
            <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-900/20 border border-blue-800/30' : 'bg-blue-50 border border-blue-200'}`}>
              <span className={`text-xs font-medium ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>💡 {item.recommendation}</span>
            </div>
          )}
          
          {/* What to Modify */}
          {item.what_to_modify && (
            <div className={`p-2 rounded-lg ${isDark ? 'bg-orange-900/20 border border-orange-800/30' : 'bg-orange-50 border border-orange-200'}`}>
              <span className={`text-xs ${isDark ? 'text-orange-400' : 'text-orange-700'}`}>🔄 {item.what_to_modify}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Main Component
const FoodAnalysisPage = () => {
  const { isDark } = useTheme();
  const fileInputRef = useRef(null);
  const mobileFileInputRef = useRef(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  
  // Analysis state
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [activeScale, setActiveScale] = useState('overview');
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const abortControllerRef = useRef(null);
  
  // "Did You Know?" facts about food and body parameters - evidence-based with references
  const foodFacts = useMemo(() => [
    { fact: "Potassium intake of 3,500mg/day is associated with 24% lower stroke risk (WHO Guidelines)", organ: "🫀 Cardiovascular", nutrient: "Potassium" },
    { fact: "Dietary nitrates in leafy greens can reduce blood pressure by 4-10 mmHg (American Heart Association)", organ: "🫀 Cardiovascular", nutrient: "Nitrates" },
    { fact: "2g/day EPA+DHA omega-3 fatty acids reduce triglycerides by 15-30% (American College of Cardiology)", organ: "🫀 Cardiovascular", nutrient: "Omega-3" },
    { fact: "3g/day soluble fiber from oats reduces LDL cholesterol by 5-10% (FDA health claim)", organ: "🫀 Cardiovascular", nutrient: "Fiber" },
    { fact: "600-800 IU Vitamin D daily improves calcium absorption by 30-40% (Endocrine Society)", organ: "🦴 Skeletal", nutrient: "Vitamin D" },
    { fact: "Lycopene bioavailability increases 2.5x when tomatoes are cooked with olive oil (Journal of Nutrition)", organ: "🛡️ Antioxidant", nutrient: "Lycopene" },
    { fact: "Piperine from black pepper increases curcumin bioavailability by 2,000% (Planta Medica)", organ: "🛡️ Immune", nutrient: "Curcumin" },
    { fact: "12-week blueberry supplementation improved memory by 16% in older adults (European Journal of Nutrition)", organ: "🧠 Neurological", nutrient: "Anthocyanins" },
    { fact: "Aged garlic extract (600-1200mg/day) reduces systolic blood pressure by 6-11 mmHg (BMC Cardiovasc Disord)", organ: "🫀 Cardiovascular", nutrient: "Allicin" },
    { fact: "EGCG in green tea increases 24-hour energy expenditure by 4% (American Journal of Clinical Nutrition)", organ: "⚡ Metabolic", nutrient: "Catechins" },
    { fact: "Dark chocolate (70%+ cocoa) improves insulin sensitivity by 20% in 15 days (American Journal of Clinical Nutrition)", organ: "🔬 Endocrine", nutrient: "Flavanols" },
    { fact: "Sulforaphane from broccoli activates Nrf2 pathway, upregulating 200+ cytoprotective genes (PNAS)", organ: "🔬 Cellular", nutrient: "Sulforaphane" },
    { fact: "Probiotic strains produce 90% of the body's serotonin in the gut (Cell)", organ: "🧠 Gut-Brain", nutrient: "Probiotics" },
    { fact: "Beetroot juice (500ml) extends time to exhaustion by 16% in cycling tests (Journal of Applied Physiology)", organ: "💪 Muscular", nutrient: "Nitrates" },
    { fact: "100mg Vitamin C with meals increases non-heme iron absorption by 67% (American Journal of Clinical Nutrition)", organ: "🩸 Hematological", nutrient: "Vitamin C" },
    { fact: "28g walnuts daily reduces total cholesterol by 4% and LDL by 5% (Circulation)", organ: "🫀 Cardiovascular", nutrient: "ALA Omega-3" },
    { fact: "1g ginger reduces nausea severity by 40% in chemotherapy patients (Support Care Cancer)", organ: "🫃 Gastrointestinal", nutrient: "Gingerols" },
    { fact: "Quercetin blocks histamine release from mast cells, reducing allergy symptoms (Molecules)", organ: "🛡️ Immune", nutrient: "Quercetin" },
    { fact: "120μg Vitamin K daily activates Matrix Gla Protein, preventing arterial calcification (Thrombosis and Haemostasis)", organ: "🫀 Cardiovascular", nutrient: "Vitamin K2" },
    { fact: "200μg selenium from 2 Brazil nuts supports thyroid T4→T3 conversion (Journal of Clinical Endocrinology)", organ: "🔬 Endocrine", nutrient: "Selenium" },
    { fact: "550mg/day choline prevents fatty liver disease and supports methylation (Institute of Medicine)", organ: "🔶 Hepatic", nutrient: "Choline" },
    { fact: "Capsaicin increases energy expenditure by 50 kcal/day through thermogenesis (British Journal of Nutrition)", organ: "⚡ Metabolic", nutrient: "Capsaicin" },
    { fact: "8-11mg zinc daily is required for 300+ enzymatic reactions (National Institutes of Health)", organ: "🔬 Cellular", nutrient: "Zinc" },
    { fact: "Resveratrol activates SIRT1 longevity gene and improves mitochondrial function (Nature)", organ: "🔬 Cellular", nutrient: "Resveratrol" },
    { fact: "Potassium:Sodium ratio of 2:1 is optimal for blood pressure control (Advances in Nutrition)", organ: "🫘 Renal", nutrient: "Electrolytes" },
    { fact: "200-300mg tryptophan from turkey is converted to serotonin and melatonin (Journal of Psychiatry & Neuroscience)", organ: "🧠 Neurological", nutrient: "Tryptophan" },
    { fact: "Beta-glucans from mushrooms increase natural killer cell activity by 40% (JAFC)", organ: "🛡️ Immune", nutrient: "Beta-glucans" },
    { fact: "400μg folate daily reduces homocysteine by 25%, lowering cardiovascular risk (American Heart Association)", organ: "🫀 Cardiovascular", nutrient: "Folate" },
    { fact: "200-1000μg chromium picolinate improves insulin sensitivity by 10-30% (Diabetes Technology & Therapeutics)", organ: "🔬 Endocrine", nutrient: "Chromium" },
    { fact: "100-200mg CoQ10 supports mitochondrial ATP production and reduces statin-induced myalgia (Mayo Clinic Proceedings)", organ: "⚡ Metabolic", nutrient: "CoQ10" },
  ], []);
  
  // Panel state
  const [showPanel, setShowPanel] = useState(false);
  const [panelTab, setPanelTab] = useState('demographics');
  
  // Diseases from database
  const [allDiseases, setAllDiseases] = useState([]);
  const [diseasesLoading, setDiseasesLoading] = useState(true);
  
  // Food images for multi-image analysis
  const [foodImages, setFoodImages] = useState([]);
  const [isAnalyzingImages, setIsAnalyzingImages] = useState(false);
  
  // Swipe navigation for organ systems
  const handleSwipeNavigation = useCallback((direction) => {
    if (!analysisResult?.organ_system_parameter_effects) return;
    
    const allSystems = Object.keys(analysisResult.organ_system_parameter_effects);
    const isOrganView = allSystems.includes(activeScale);
    
    if (!isOrganView) return;
    
    const currentIndex = allSystems.indexOf(activeScale);
    
    if (direction === 'left' && currentIndex < allSystems.length - 1) {
      setActiveScale(allSystems[currentIndex + 1]);
    } else if (direction === 'right') {
      if (currentIndex > 0) {
        setActiveScale(allSystems[currentIndex - 1]);
      } else {
        // Swipe right from first organ goes back to overview
        setActiveScale('overview');
      }
    }
  }, [analysisResult, activeScale]);
  
  // Set up swipe handlers
  const swipeHandlers = useSwipe(
    () => handleSwipeNavigation('left'),
    () => handleSwipeNavigation('right'),
    50
  );
  
  // Patient profile - comprehensive
  const [profile, setProfile] = useState({
    // Demographics
    age: '',
    sex: '',
    weight: '',
    height: '',
    activityLevel: 'moderate',
    // Medical
    conditions: [],
    medications: [],
    allergies: [],
    // Dietary
    dietaryPrefs: [],
    // Food intake
    foodIntake: '',
  });
  
  // Disease search
  const [diseaseSearch, setDiseaseSearch] = useState('');
  const [medSearch, setMedSearch] = useState('');
  
  // Fetch diseases from database on mount
  useEffect(() => {
    const fetchDiseases = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/diseases-db/list`);
        if (response.ok) {
          const data = await response.json();
          // Extract disease names from the response
          const diseaseNames = (data.diseases || []).map(d => d.name);
          setAllDiseases(diseaseNames);
        } else {
          console.warn('Failed to fetch diseases, using fallback list');
          setAllDiseases(FALLBACK_DISEASES);
        }
      } catch (error) {
        console.error('Error fetching diseases:', error);
        setAllDiseases(FALLBACK_DISEASES);
      } finally {
        setDiseasesLoading(false);
      }
    };
    fetchDiseases();
  }, []);
  
  // Food Browser state
  const [showFoodBrowser, setShowFoodBrowser] = useState(false);
  const [foodBrowserData, setFoodBrowserData] = useState(null);
  const [foodBrowserLoading, setFoodBrowserLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [foodBrowserSearch, setFoodBrowserSearch] = useState('');
  
  // Body Map state
  const [showBodyMap, setShowBodyMap] = useState(false);
  
  // Serving Size / Quantity Calculator state
  const [servingMultiplier, setServingMultiplier] = useState(1);
  const [showQuantityPanel, setShowQuantityPanel] = useState(false);
  
  // Calculate scaled nutrients based on serving multiplier
  const scaledAnalysisResult = useMemo(() => {
    if (!analysisResult || servingMultiplier === 1) return analysisResult;
    
    const scaled = { ...analysisResult };
    
    // Get base serving in grams (parse from string like "100g" or "3 oz (85g)" or just use 100g default)
    let baseServingGrams = 100;
    const servingStr = analysisResult.serving_size || analysisResult.serving || '';
    const gramsMatch = servingStr.match(/(\d+)\s*g/i);
    if (gramsMatch) {
      baseServingGrams = parseInt(gramsMatch[1]);
    }
    
    // Scale nutrients
    if (scaled.nutrients) {
      scaled.scaledNutrients = {};
      Object.entries(scaled.nutrients).forEach(([key, value]) => {
        if (typeof value === 'number') {
          scaled.scaledNutrients[key] = Math.round(value * servingMultiplier * 10) / 10;
        } else if (typeof value === 'object' && value?.value) {
          scaled.scaledNutrients[key] = {
            ...value,
            value: Math.round(value.value * servingMultiplier * 10) / 10,
            originalValue: value.value
          };
        }
      });
    }
    
    // Scale macros - check different possible field names and locations
    const nutritionalProfile = scaled.complete_nutritional_profile || {};
    const macrosFromProfile = nutritionalProfile.macronutrients || {};
    const macros = scaled.macronutrients || scaled.macros || macrosFromProfile || {};
    const foods = scaled.foods_analyzed || [];
    
    // If macros not directly available, try to get from first analyzed food
    let calories = macros.energy_kcal || macros.calories || macros.energy;
    let protein = macros.protein_g || macros.protein;
    let carbs = macros.carbohydrates_g || macros.carbohydrates || macros.carbs;
    let fat = macros.fat_total_g || macros.fat || macros.total_fat;
    let fiber = macros.fiber_g || macros.fiber || macros.dietary_fiber;
    
    // Try to get from foods_analyzed if not in macros
    if (foods.length > 0 && (!calories && !protein)) {
      const food = foods[0];
      const nutrients = food.nutrients || {};
      calories = calories || nutrients.calories || nutrients.energy_kcal || 0;
      protein = protein || nutrients.protein || 0;
      carbs = carbs || nutrients.carbohydrates || nutrients.carbs || 0;
      fat = fat || nutrients.fat || nutrients.total_fat || 0;
      fiber = fiber || nutrients.fiber || nutrients.dietary_fiber || 0;
    }
    
    scaled.scaledMacros = {
      calories: Math.round((calories || 0) * servingMultiplier),
      protein: Math.round((protein || 0) * servingMultiplier * 10) / 10,
      carbs: Math.round((carbs || 0) * servingMultiplier * 10) / 10,
      fat: Math.round((fat || 0) * servingMultiplier * 10) / 10,
      fiber: Math.round((fiber || 0) * servingMultiplier * 10) / 10
    };
    
    // Scale organ effects (impact increases with quantity)
    if (scaled.organ_system_parameter_effects) {
      scaled.scaledOrganEffects = {};
      Object.entries(scaled.organ_system_parameter_effects).forEach(([organ, effects]) => {
        scaled.scaledOrganEffects[organ] = {
          ...effects,
          impact_multiplier: servingMultiplier,
          // Effects scale non-linearly (diminishing returns)
          scaled_impact: Math.min(100, (effects.impact || 50) * Math.pow(servingMultiplier, 0.7))
        };
      });
    }
    
    scaled.servingMultiplier = servingMultiplier;
    scaled.baseServingGrams = baseServingGrams;
    scaled.originalServing = servingStr || `${baseServingGrams}g`;
    scaled.adjustedServing = `${Math.round(baseServingGrams * servingMultiplier)}g`;
    
    return scaled;
  }, [analysisResult, servingMultiplier]);
  
  // Build body map effects from analysis result
  const bodyMapEffects = useMemo(() => {
    if (!analysisResult?.foods_analyzed) return [];
    
    const effects = [];
    
    // Collect organ effects from all analyzed foods
    analysisResult.foods_analyzed.forEach(food => {
      const organEffects = food.organ_effects || {};
      
      Object.entries(organEffects).forEach(([organ, data]) => {
        if (data && typeof data === 'object') {
          // Add each parameter effect
          Object.entries(data).forEach(([param, value]) => {
            if (param !== 'effect' && value !== null && value !== undefined) {
              effects.push({
                organ: organ,
                parameter: param,
                change: value > 0 ? 'increase' : value < 0 ? 'decrease' : 'neutral',
                magnitude: Math.abs(value) * 10, // Scale for visibility
                value: value,
                unit: '%',
                effect: data.effect || 'neutral',
                source: food.name
              });
            }
          });
        }
      });
    });
    
    return effects;
  }, [analysisResult]);
  
  // Build body map connections from analysis result
  const bodyMapConnections = useMemo(() => {
    if (!analysisResult?.foods_analyzed) return [];
    
    const connections = [];
    
    analysisResult.foods_analyzed.forEach(food => {
      const organEffects = food.organ_effects || {};
      
      Object.entries(organEffects).forEach(([organ, data]) => {
        if (data && typeof data === 'object') {
          const effectType = data.effect || 'neutral';
          // Calculate strength based on number of parameters affected
          const paramCount = Object.keys(data).filter(k => k !== 'effect' && data[k]).length;
          const strength = Math.min(100, paramCount * 20);
          
          connections.push({
            source: food.name,
            organ: organ,
            strength: strength,
            effect: effectType === 'beneficial' ? 'beneficial' : effectType === 'caution' ? 'harmful' : 'neutral'
          });
        }
      });
    });
    
    return connections;
  }, [analysisResult]);
  
  // Fetch food browser data
  const fetchFoodBrowserData = useCallback(async () => {
    if (foodBrowserData) return; // Already loaded
    setFoodBrowserLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/food/browse-all`);
      if (response.ok) {
        const data = await response.json();
        setFoodBrowserData(data);
      }
    } catch (error) {
      console.error('Error fetching food browser data:', error);
    } finally {
      setFoodBrowserLoading(false);
    }
  }, [foodBrowserData]);
  
  // Load food browser data when opened
  useEffect(() => {
    if (showFoodBrowser && !foodBrowserData) {
      fetchFoodBrowserData();
    }
  }, [showFoodBrowser, foodBrowserData, fetchFoodBrowserData]);
  
  // Filter foods in browser based on search
  const filteredFoodBrowserData = useMemo(() => {
    if (!foodBrowserData?.categories) return null;
    if (!foodBrowserSearch.trim()) return foodBrowserData;
    
    const searchLower = foodBrowserSearch.toLowerCase();
    const filtered = { ...foodBrowserData, categories: {} };
    let totalFiltered = 0;
    
    for (const [cat, data] of Object.entries(foodBrowserData.categories)) {
      const matchingFoods = data.foods.filter(f => f.toLowerCase().includes(searchLower));
      if (matchingFoods.length > 0) {
        filtered.categories[cat] = {
          ...data,
          foods: matchingFoods,
          count: matchingFoods.length
        };
        totalFiltered += matchingFoods.length;
      }
    }
    
    filtered.total_foods = totalFiltered;
    return filtered;
  }, [foodBrowserData, foodBrowserSearch]);
  
  // Handle clicking on a food in the browser
  const handleFoodBrowserSelect = (foodName) => {
    setSearchQuery(foodName);
    setShowFoodBrowser(false);
    // Trigger analysis
    analyzeFood(foodName);
  };
  
  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  
  // Filtered diseases from database
  const filteredDiseases = useMemo(() => {
    const diseases = allDiseases.length > 0 ? allDiseases : FALLBACK_DISEASES;
    if (!diseaseSearch) return diseases.slice(0, 10);
    return diseases.filter(d => 
      d.toLowerCase().includes(diseaseSearch.toLowerCase()) &&
      !profile.conditions.includes(d)
    ).slice(0, 10);
  }, [diseaseSearch, profile.conditions, allDiseases]);
  
  // Filtered medications
  const filteredMeds = useMemo(() => {
    if (!medSearch) return COMMON_MEDICATIONS.slice(0, 8);
    return COMMON_MEDICATIONS.filter(m => 
      m.toLowerCase().includes(medSearch.toLowerCase()) &&
      !profile.medications.includes(m)
    ).slice(0, 8);
  }, [medSearch, profile.medications]);
  
  // BMI calculation
  const bmi = useMemo(() => {
    if (profile.weight && profile.height) {
      const h = parseFloat(profile.height) / 100;
      return (parseFloat(profile.weight) / (h * h)).toFixed(1);
    }
    return null;
  }, [profile.weight, profile.height]);
  
  // Cycle through facts while analyzing - slower for readability
  useEffect(() => {
    if (isAnalyzing || isAnalyzingImages) {
      const interval = setInterval(() => {
        setCurrentFactIndex(prev => (prev + 1) % foodFacts.length);
      }, 8000); // Change fact every 8 seconds for better readability
      return () => clearInterval(interval);
    }
  }, [isAnalyzing, isAnalyzingImages, foodFacts.length]);
  
  // Cancel analysis function
  const cancelAnalysis = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsAnalyzing(false);
    setIsAnalyzingImages(false);
    toast.info('Analysis cancelled');
  }, []);
  
  // Handle image upload
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Limit to 10 images at a time
    const filesToProcess = files.slice(0, 10 - foodImages.length);
    
    filesToProcess.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFoodImages(prev => [...prev, {
          id: `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          file,
          preview: e.target.result,
          name: file.name
        }]);
      };
      reader.readAsDataURL(file);
    });
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Remove image
  const removeImage = (imageId) => {
    setFoodImages(prev => prev.filter(img => img.id !== imageId));
  };
  
  // Analyze images with AI (with timeout handling)
  const analyzeImages = async () => {
    if (foodImages.length === 0) {
      toast.error('Please add at least one food image');
      return;
    }
    
    setIsAnalyzingImages(true);
    setAnalysisResult(null);
    setActiveScale('overview');
    setShowPanel(false);
    
    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout
    
    try {
      // Convert images to base64 for API
      const imageData = foodImages.map(img => ({
        name: img.name,
        data: img.preview.split(',')[1] // Remove data:image/xxx;base64, prefix
      }));
      
      const response = await fetch(`${BACKEND_URL}/api/food/analyze-images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          images: imageData,
          health_profile: profile.conditions.length > 0 || profile.medications.length > 0 ? {
            age: profile.age,
            sex: profile.sex,
            weight: profile.weight,
            height: profile.height,
            conditions: profile.conditions,
            medications: profile.medications,
            allergies: profile.allergies
          } : null
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      const data = await response.json();
      if (data.success) {
        setAnalysisResult(data.analysis);
        toast.success(`Analyzed ${foodImages.length} food image${foodImages.length > 1 ? 's' : ''}`);
      } else {
        throw new Error(data.detail || 'Image analysis failed');
      }
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Image analysis error:', error);
      if (error.name === 'AbortError') {
        toast.error('Image analysis timed out. Please try with fewer or smaller images.');
      } else if (error.message?.includes('502') || error.message?.includes('503') || error.message?.includes('Gateway')) {
        toast.error('AI service temporarily unavailable. Please try again in a moment.');
      } else {
        toast.error(`Analysis failed: ${error.message || 'Unknown error'}. Please try again.`);
      }
    } finally {
      setIsAnalyzingImages(false);
    }
  };

  // Smart search
  const smartSearch = useCallback((query) => {
    if (!query.trim() || query.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    
    setShowResults(true);
    const q = query.toLowerCase();
    
    const results = [
      { id: `search_${q}`, name: query, type: 'search' }
    ];
    
    // Add matching suggestions
    const allFoods = [
      ...QUICK_FOODS,
      'Pizza', 'Burger', 'Pasta', 'Bread', 'Milk', 'Cheese', 'Yogurt',
      'Potassium', 'Sodium', 'Magnesium', 'Calcium', 'Iron', 'Zinc',
      'Vitamin C', 'Vitamin B12', 'Omega-3', 'Fiber', 'Protein Shake',
      'Coca-Cola', 'Orange Juice', 'Apple', 'Grapes', 'Watermelon'
    ];
    
    allFoods.forEach(food => {
      if (food.toLowerCase().includes(q) && food.toLowerCase() !== q) {
        results.push({ id: `suggest_${food}`, name: food, type: 'suggestion' });
      }
    });
    
    setSearchResults(results.slice(0, 8));
  }, []);
  
  useEffect(() => {
    const timer = setTimeout(() => smartSearch(searchQuery), 150);
    return () => clearTimeout(timer);
  }, [searchQuery, smartSearch]);
  
  // Analyze food - DATABASE FIRST, then AI fallback
  const analyzeFood = async (foodName) => {
    setShowResults(false);
    setSearchQuery(foodName);
    setIsAnalyzing(true);
    setAnalysisResult(null);
    setActiveScale('overview');
    setCurrentFactIndex(Math.floor(Math.random() * foodFacts.length)); // Start with random fact
    
    // Cancel any existing analysis
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    const healthProfile = profile.conditions.length > 0 || profile.medications.length > 0 || profile.allergies.length > 0 ? {
      age: profile.age,
      sex: profile.sex,
      weight: profile.weight,
      height: profile.height,
      conditions: profile.conditions,
      medications: profile.medications,
      allergies: profile.allergies,
      dietary_restrictions: profile.dietaryPrefs
    } : null;
    
    try {
      // STEP 1: Try database-driven analysis first (fast, no AI)
      const dbResponse = await fetch(`${BACKEND_URL}/api/food/analyze-from-db`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          food_name: foodName,
          serving_size: '1 standard serving',
          health_profile: healthProfile
        }),
        signal: abortControllerRef.current.signal
      });
      
      const dbData = await dbResponse.json();
      
      if (dbData.success && dbData.in_database) {
        // Found in database - instant results!
        setAnalysisResult(dbData.analysis);
        setIsAnalyzing(false);
        return;
      }
      
      // STEP 2: Not in database - silently use ingredient breakdown analysis
      // No user-facing message about AI - it's a seamless experience
      console.log('Analyzing dish components...');
      
      const timeoutId = setTimeout(() => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      }, 120000);
      
      const aiResponse = await fetch(`${BACKEND_URL}/api/food/analyze-product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_name: foodName,
          serving_size: '1 standard serving',
          health_profile: healthProfile
        }),
        signal: abortControllerRef.current.signal
      });
      
      clearTimeout(timeoutId);
      
      const aiData = await aiResponse.json();
      if (aiData.success) {
        setAnalysisResult(aiData.analysis);
        // Silent success - no toast needed
      } else {
        throw new Error(aiData.detail || 'Analysis failed');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      if (error.name === 'AbortError') {
        toast.error('Analysis timed out. Please try again.');
      } else {
        toast.error('Analysis failed. Please check your connection and try again.');
      }
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  // Analyze multi-food intake with timeout handling
  const analyzeIntake = async () => {
    if (!profile.foodIntake.trim()) {
      toast.error('Please enter your food intake');
      return;
    }
    
    setIsAnalyzing(true);
    setAnalysisResult(null);
    setActiveScale('overview');
    setShowPanel(false);
    
    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/food/analyze-daily`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          daily_intake_text: profile.foodIntake,
          health_profile: profile.conditions.length > 0 || profile.medications.length > 0 ? {
            age: profile.age,
            sex: profile.sex,
            weight: profile.weight,
            height: profile.height,
            conditions: profile.conditions,
            medications: profile.medications,
            allergies: profile.allergies
          } : null
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      const data = await response.json();
      if (data.success) {
        setAnalysisResult(data.analysis);
        toast.success('Food intake analysis complete');
      } else {
        throw new Error(data.detail || 'Analysis failed');
      }
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Analysis error:', error);
      if (error.name === 'AbortError') {
        toast.error('Analysis timed out. Please try again.');
      } else {
        toast.error('Analysis failed. Please check your connection and try again.');
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Get parameter effects for a specific system
  const getSystemParams = (systemId) => {
    if (!analysisResult?.organ_system_parameter_effects) return null;
    return analysisResult.organ_system_parameter_effects[systemId];
  };

  return (
    <>
      <Helmet>
        <title>Π Food Analysis | PhiCor.ai</title>
      </Helmet>
      
      <div className={`min-h-screen ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
        <FeatureNav />
        
        <div className="flex h-[calc(100vh-64px)]">
          {/* Left Panel - Patient Profile (Desktop) */}
          <div className={`hidden lg:flex flex-col w-80 flex-shrink-0 border-r overflow-hidden ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'}`}>
            <div className="p-4 flex-1 overflow-y-auto">
              <h3 className={`text-sm font-semibold flex items-center gap-2 mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <User className="w-4 h-4 text-green-500" />
                Subject Profile
              </h3>
              
              <Tabs defaultValue="demographics" className="w-full">
                <TabsList className="w-full grid grid-cols-4 h-8 mb-3">
                  <TabsTrigger value="demographics" className="text-[9px] px-1">Bio</TabsTrigger>
                  <TabsTrigger value="medical" className="text-[9px] px-1">Medical</TabsTrigger>
                  <TabsTrigger value="dietary" className="text-[9px] px-1">Diet</TabsTrigger>
                  <TabsTrigger value="intake" className="text-[9px] px-1">Intake</TabsTrigger>
                </TabsList>
                
                {/* Demographics Tab */}
                <TabsContent value="demographics" className="space-y-3 mt-0">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={`text-[10px] font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Age</label>
                      <Input
                        type="number"
                        value={profile.age}
                        onChange={(e) => setProfile(p => ({ ...p, age: e.target.value }))}
                        placeholder="Years"
                        className={`h-8 text-xs mt-1 ${isDark ? 'bg-gray-800 border-gray-700' : ''}`}
                      />
                    </div>
                    <div>
                      <label className={`text-[10px] font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Sex</label>
                      <select
                        value={profile.sex}
                        onChange={(e) => setProfile(p => ({ ...p, sex: e.target.value }))}
                        className={`w-full h-8 text-xs mt-1 rounded-md border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
                      >
                        <option value="">Select</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>
                    <div>
                      <label className={`text-[10px] font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Weight (kg)</label>
                      <Input
                        type="number"
                        value={profile.weight}
                        onChange={(e) => setProfile(p => ({ ...p, weight: e.target.value }))}
                        placeholder="kg"
                        className={`h-8 text-xs mt-1 ${isDark ? 'bg-gray-800 border-gray-700' : ''}`}
                      />
                    </div>
                    <div>
                      <label className={`text-[10px] font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Height (cm)</label>
                      <Input
                        type="number"
                        value={profile.height}
                        onChange={(e) => setProfile(p => ({ ...p, height: e.target.value }))}
                        placeholder="cm"
                        className={`h-8 text-xs mt-1 ${isDark ? 'bg-gray-800 border-gray-700' : ''}`}
                      />
                    </div>
                  </div>
                  
                  {/* BMI Display */}
                  {bmi && (
                    <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-green-50'}`}>
                      <div className="flex justify-between items-center">
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>BMI</span>
                        <span className={`text-lg font-bold ${
                          parseFloat(bmi) < 18.5 ? 'text-blue-500' :
                          parseFloat(bmi) < 25 ? 'text-green-500' :
                          parseFloat(bmi) < 30 ? 'text-yellow-500' : 'text-red-500'
                        }`}>{bmi}</span>
                      </div>
                      <div className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {parseFloat(bmi) < 18.5 ? 'Underweight' :
                         parseFloat(bmi) < 25 ? 'Normal' :
                         parseFloat(bmi) < 30 ? 'Overweight' : 'Obese'}
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label className={`text-[10px] font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Activity Level</label>
                    <div className="grid grid-cols-3 gap-1 mt-1">
                      {['sedentary', 'moderate', 'active'].map(level => (
                        <button
                          key={level}
                          onClick={() => setProfile(p => ({ ...p, activityLevel: level }))}
                          className={`p-2 rounded text-[10px] capitalize border ${
                            profile.activityLevel === level
                              ? 'bg-green-500/20 border-green-500/50 text-green-400'
                              : isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'
                          }`}
                        >
                          {level}
                        </button>
                      ))}
                    </div>
                  </div>
                </TabsContent>
                
                {/* Medical Tab */}
                <TabsContent value="medical" className="space-y-3 mt-0">
                  {/* Conditions Search */}
                  <div>
                    <label className={`text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      Medical Conditions
                    </label>
                    <Input
                      value={diseaseSearch}
                      onChange={(e) => setDiseaseSearch(e.target.value)}
                      placeholder="Search diseases..."
                      className={`h-8 text-xs mt-1 ${isDark ? 'bg-gray-800 border-gray-700' : ''}`}
                    />
                    <div className="flex flex-wrap gap-1 mt-2 max-h-24 overflow-y-auto">
                      {filteredDiseases.map(d => (
                        <button
                          key={d}
                          onClick={() => {
                            if (!profile.conditions.includes(d)) {
                              setProfile(p => ({ ...p, conditions: [...p.conditions, d] }));
                            }
                            setDiseaseSearch('');
                          }}
                          className={`px-2 py-1 rounded text-[9px] border ${isDark ? 'bg-gray-800 border-gray-700 hover:bg-gray-700' : 'bg-gray-100 border-gray-200 hover:bg-gray-200'}`}
                        >
                          + {d}
                        </button>
                      ))}
                    </div>
                    {/* Selected conditions */}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {profile.conditions.map((c, i) => (
                        <Badge key={i} className="text-[9px] bg-blue-500/20 text-blue-400 cursor-pointer"
                          onClick={() => setProfile(p => ({ ...p, conditions: p.conditions.filter((_, idx) => idx !== i) }))}>
                          {c} <X className="w-2 h-2 ml-1" />
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  {/* Medications Search */}
                  <div>
                    <label className={`text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      Current Medications
                    </label>
                    <Input
                      value={medSearch}
                      onChange={(e) => setMedSearch(e.target.value)}
                      placeholder="Search or type medication..."
                      className={`h-8 text-xs mt-1 ${isDark ? 'bg-gray-800 border-gray-700' : ''}`}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && medSearch.trim() && !profile.medications.includes(medSearch.trim())) {
                          setProfile(p => ({ ...p, medications: [...p.medications, medSearch.trim()] }));
                          setMedSearch('');
                        }
                      }}
                    />
                    <div className="flex flex-wrap gap-1 mt-2">
                      {filteredMeds.map(m => (
                        <button
                          key={m}
                          onClick={() => {
                            if (!profile.medications.includes(m)) {
                              setProfile(p => ({ ...p, medications: [...p.medications, m] }));
                            }
                            setMedSearch('');
                          }}
                          className={`px-2 py-1 rounded text-[9px] border ${isDark ? 'bg-gray-800 border-gray-700 hover:bg-gray-700' : 'bg-gray-100 border-gray-200 hover:bg-gray-200'}`}
                        >
                          + {m}
                        </button>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {profile.medications.map((m, i) => (
                        <Badge key={i} className="text-[9px] bg-purple-500/20 text-purple-400 cursor-pointer"
                          onClick={() => setProfile(p => ({ ...p, medications: p.medications.filter((_, idx) => idx !== i) }))}>
                          {m} <X className="w-2 h-2 ml-1" />
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  {/* Allergies */}
                  <div>
                    <label className={`text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      Food Allergies
                    </label>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {COMMON_ALLERGIES.map(a => (
                        <button
                          key={a}
                          onClick={() => {
                            setProfile(p => ({
                              ...p,
                              allergies: p.allergies.includes(a) 
                                ? p.allergies.filter(x => x !== a)
                                : [...p.allergies, a]
                            }));
                          }}
                          className={`px-2 py-1 rounded text-[9px] border ${
                            profile.allergies.includes(a)
                              ? 'bg-red-500/20 border-red-500/50 text-red-400'
                              : isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'
                          }`}
                        >
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>
                </TabsContent>
                
                {/* Dietary Tab */}
                <TabsContent value="dietary" className="space-y-3 mt-0">
                  <div>
                    <label className={`text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      Dietary Preferences
                    </label>
                    <div className="grid grid-cols-2 gap-1 mt-2">
                      {DIETARY_PREFS.map(pref => (
                        <button
                          key={pref.id}
                          onClick={() => {
                            setProfile(p => ({
                              ...p,
                              dietaryPrefs: p.dietaryPrefs.includes(pref.id)
                                ? p.dietaryPrefs.filter(x => x !== pref.id)
                                : [...p.dietaryPrefs, pref.id]
                            }));
                          }}
                          className={`p-2 rounded text-[10px] flex items-center gap-1 border ${
                            profile.dietaryPrefs.includes(pref.id)
                              ? 'bg-green-500/20 border-green-500/50 text-green-400'
                              : isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'
                          }`}
                        >
                          <span>{pref.icon}</span>
                          <span>{pref.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </TabsContent>
                
                {/* Food Intake Tab */}
                <TabsContent value="intake" className="space-y-3 mt-0">
                  <div className={`p-2 rounded-lg text-[10px] ${isDark ? 'bg-green-900/20 text-green-400' : 'bg-green-50 text-green-700'}`}>
                    💡 Enter what you ate or upload food photos for AI analysis
                  </div>
                  
                  {/* Text Intake */}
                  <Textarea
                    value={profile.foodIntake}
                    onChange={(e) => setProfile(p => ({ ...p, foodIntake: e.target.value }))}
                    placeholder="Example:
Breakfast: 2 eggs, toast, orange juice
Lunch: Chicken salad, bread roll
Dinner: Salmon with rice and vegetables
Snacks: Apple, almonds, green tea"
                    className={`min-h-[120px] text-xs ${isDark ? 'bg-gray-800 border-gray-700' : ''}`}
                  />
                  <Button 
                    onClick={analyzeIntake}
                    disabled={isAnalyzing || !profile.foodIntake.trim()}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-500"
                  >
                    {isAnalyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    Analyze Text Intake
                  </Button>
                  
                  {/* Divider */}
                  <div className="flex items-center gap-2 py-1">
                    <div className={`flex-1 h-px ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                    <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>OR</span>
                    <div className={`flex-1 h-px ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                  </div>
                  
                  {/* Image Upload Section */}
                  <div>
                    <label className={`text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      📸 Upload Food Photos
                    </label>
                    <p className={`text-[9px] mt-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                      Upload photos of your meals for AI analysis (up to 10 images)
                    </p>
                    
                    {/* Hidden file input */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/*"
                      multiple
                      className="hidden"
                    />
                    
                    {/* Upload button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={foodImages.length >= 10}
                      className={`w-full mt-2 h-20 border-dashed flex flex-col gap-1 ${isDark ? 'border-gray-700 hover:bg-gray-800' : ''}`}
                    >
                      <Camera className="w-5 h-5 text-green-500" />
                      <span className="text-xs">Add Photos</span>
                      <span className="text-[9px] text-gray-500">{foodImages.length}/10 images</span>
                    </Button>
                    
                    {/* Image Previews */}
                    {foodImages.length > 0 && (
                      <div className="mt-2 grid grid-cols-4 gap-1">
                        {foodImages.map((img) => (
                          <div key={img.id} className="relative group aspect-square">
                            <img
                              src={img.preview}
                              alt={img.name}
                              className="w-full h-full object-cover rounded"
                            />
                            <button
                              onClick={() => removeImage(img.id)}
                              className="absolute top-0 right-0 p-0.5 bg-red-500 rounded-bl text-white opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Analyze Images Button */}
                    {foodImages.length > 0 && (
                      <Button 
                        onClick={analyzeImages}
                        disabled={isAnalyzingImages}
                        className="w-full mt-2 bg-gradient-to-r from-purple-500 to-pink-500"
                      >
                        {isAnalyzingImages ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Image className="w-4 h-4 mr-2" />
                        )}
                        Analyze {foodImages.length} Photo{foodImages.length > 1 ? 's' : ''}
                      </Button>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Header */}
            <div className={`p-3 sm:p-4 border-b ${isDark ? 'bg-gray-900/30 border-gray-800' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center gap-2 sm:gap-3">
                {/* Mobile menu button */}
                <Button variant="ghost" size="sm" className="lg:hidden" onClick={() => setShowPanel(true)}>
                  <Menu className="w-5 h-5" />
                </Button>
                
                {/* Pi Icon */}
                <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                  <span className="text-white text-xl font-bold">Π</span>
                </div>
                
                {/* Search */}
                <div className="flex-1 relative">
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => searchQuery.length >= 2 && setShowResults(true)}
                    onKeyPress={(e) => e.key === 'Enter' && searchQuery.trim() && analyzeFood(searchQuery)}
                    placeholder="Search food, dish, vitamin, nutrient..."
                    className={`pr-10 ${isDark ? 'bg-gray-800 border-gray-700' : ''}`}
                  />
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  
                  {/* Search Dropdown - only show when no analysis result */}
                  {showResults && searchResults.length > 0 && !analysisResult && !isAnalyzing && (
                    <div className={`absolute z-50 left-0 right-0 mt-2 rounded-xl border shadow-xl overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                      {searchResults.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => analyzeFood(item.name)}
                          className={`w-full p-3 text-left flex items-center gap-3 border-b last:border-0 ${isDark ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-100 hover:bg-gray-50'}`}
                        >
                          <span className="text-lg">{getFoodIcon(item.name)}</span>
                          <div className="flex-1">
                            <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.name}</div>
                            <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                              {item.type === 'search' ? 'Analyze with PhiCor' : 'Quick suggestion'}
                            </div>
                          </div>
                          <Badge className="text-[9px] bg-green-500/20 text-green-500">Π</Badge>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Food Browser Toggle */}
                <Button
                  variant={showFoodBrowser ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowFoodBrowser(!showFoodBrowser)}
                  className={`ml-2 hidden lg:flex items-center gap-1.5 ${showFoodBrowser ? 'bg-green-500 hover:bg-green-600' : ''}`}
                >
                  <Apple className="w-4 h-4" />
                  <span className="text-xs">Browse Foods</span>
                  {foodBrowserData && (
                    <Badge className="text-[9px] bg-white/20">{foodBrowserData.total_foods?.toLocaleString()}</Badge>
                  )}
                </Button>
              </div>
              
              {/* Profile badges */}
              {(profile.conditions.length > 0 || profile.medications.length > 0 || profile.allergies.length > 0) && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {profile.conditions.slice(0, 3).map((c, i) => (
                    <Badge key={i} className="text-[9px] bg-blue-500/20 text-blue-400">{c}</Badge>
                  ))}
                  {profile.medications.slice(0, 2).map((m, i) => (
                    <Badge key={i} className="text-[9px] bg-purple-500/20 text-purple-400">{m}</Badge>
                  ))}
                  {profile.allergies.slice(0, 2).map((a, i) => (
                    <Badge key={i} className="text-[9px] bg-red-500/20 text-red-400">{a}</Badge>
                  ))}
                  {(profile.conditions.length + profile.medications.length + profile.allergies.length > 7) && (
                    <Badge variant="outline" className="text-[9px]">+{profile.conditions.length + profile.medications.length + profile.allergies.length - 7} more</Badge>
                  )}
                </div>
              )}
            </div>
            
            {/* Multi-Scale Analysis Tabs */}
            {analysisResult && (
              <div className={`px-3 sm:px-4 py-2 border-b overflow-x-auto ${isDark ? 'bg-gray-900/20 border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex gap-1">
                  {ANALYSIS_SCALES
                    .filter(scale => {
                      // Hide "items" tab if no multi-food data (individual_foods or combined_meal_analysis)
                      if (scale.id === 'items') {
                        return analysisResult.individual_foods?.length > 0 || 
                               analysisResult.combined_meal_analysis;
                      }
                      return true;
                    })
                    .map(scale => (
                    <button
                      key={scale.id}
                      onClick={() => { setActiveScale(scale.id); setShowBodyMap(false); }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap flex items-center gap-1 transition-all ${
                        activeScale === scale.id && !showBodyMap
                          ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                          : isDark ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <span>{scale.icon}</span>
                      <span className="hidden sm:inline">{scale.name}</span>
                    </button>
                  ))}
                  
                  {/* Body Map Toggle */}
                  <button
                    onClick={() => setShowBodyMap(!showBodyMap)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap flex items-center gap-1 transition-all ml-2 ${
                      showBodyMap
                        ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg'
                        : isDark ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid="body-map-toggle"
                  >
                    <Layers className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Body Map</span>
                  </button>
                  
                  {/* Quantity Calculator Toggle */}
                  <button
                    onClick={() => setShowQuantityPanel(!showQuantityPanel)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap flex items-center gap-1 transition-all ${
                      showQuantityPanel
                        ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg'
                        : isDark ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid="quantity-calculator-toggle"
                  >
                    <Scale className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Quantity</span>
                    {servingMultiplier !== 1 && (
                      <Badge className="ml-1 bg-white/20 text-white text-[10px] px-1">
                        {servingMultiplier}x
                      </Badge>
                    )}
                  </button>
                </div>
                
                {/* Quantity Calculator Panel */}
                <AnimatePresence>
                  {showQuantityPanel && analysisResult && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className={`overflow-hidden border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}
                    >
                      <div className={`p-3 ${isDark ? 'bg-gradient-to-r from-orange-900/20 to-amber-900/20' : 'bg-gradient-to-r from-orange-50 to-amber-50'}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Scale className="w-4 h-4 text-orange-500" />
                            <span className="text-sm font-medium">Serving Size Calculator</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              Base: {scaledAnalysisResult?.originalServing || '100g'}
                            </Badge>
                            <Badge className="bg-orange-500 text-white text-xs">
                              Adjusted: {scaledAnalysisResult?.adjustedServing || '100g'}
                            </Badge>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="flex-1">
                            <Slider
                              value={[servingMultiplier]}
                              onValueChange={(values) => setServingMultiplier(values[0])}
                              min={0.25}
                              max={5}
                              step={0.25}
                              className="w-full"
                            />
                            <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                              <span>¼</span>
                              <span>1x</span>
                              <span>2x</span>
                              <span>3x</span>
                              <span>5x</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setServingMultiplier(Math.max(0.25, servingMultiplier - 0.25))}
                              className={`w-8 h-8 rounded-lg flex items-center justify-center text-lg font-bold ${
                                isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                              }`}
                            >
                              -
                            </button>
                            <div className={`w-16 text-center py-1.5 rounded-lg font-bold ${
                              isDark ? 'bg-gray-800' : 'bg-white border'
                            }`}>
                              {servingMultiplier}x
                            </div>
                            <button
                              onClick={() => setServingMultiplier(Math.min(5, servingMultiplier + 0.25))}
                              className={`w-8 h-8 rounded-lg flex items-center justify-center text-lg font-bold ${
                                isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                              }`}
                            >
                              +
                            </button>
                            <button
                              onClick={() => setServingMultiplier(1)}
                              className="text-xs text-muted-foreground hover:text-foreground"
                            >
                              Reset
                            </button>
                          </div>
                        </div>
                        
                        {/* Quick preview of scaled values */}
                        {servingMultiplier !== 1 && scaledAnalysisResult?.scaledMacros && (
                          <div className="mt-3 pt-3 border-t border-border/50">
                            <div className="text-xs text-muted-foreground mb-2">Scaled Macronutrients ({servingMultiplier}x serving):</div>
                            <div className="grid grid-cols-5 gap-2 text-center">
                              <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                                <div className="text-lg font-bold text-orange-500">{scaledAnalysisResult.scaledMacros.calories}</div>
                                <div className="text-[10px] text-muted-foreground">kcal</div>
                              </div>
                              <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                                <div className="text-lg font-bold text-red-500">{scaledAnalysisResult.scaledMacros.protein}g</div>
                                <div className="text-[10px] text-muted-foreground">Protein</div>
                              </div>
                              <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                                <div className="text-lg font-bold text-blue-500">{scaledAnalysisResult.scaledMacros.carbs}g</div>
                                <div className="text-[10px] text-muted-foreground">Carbs</div>
                              </div>
                              <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                                <div className="text-lg font-bold text-yellow-500">{scaledAnalysisResult.scaledMacros.fat}g</div>
                                <div className="text-[10px] text-muted-foreground">Fat</div>
                              </div>
                              <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                                <div className="text-lg font-bold text-green-500">{scaledAnalysisResult.scaledMacros.fiber}g</div>
                                <div className="text-[10px] text-muted-foreground">Fiber</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
            
            {/* Content */}
            <ScrollArea className="flex-1">
              <div className="p-3 sm:p-4">
                {(isAnalyzing || isAnalyzingImages) ? (
                  <div className={`rounded-xl border p-6 sm:p-8 ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'}`}>
                    {/* Spinner and Title */}
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 mx-auto mb-4 relative">
                        <div className="w-16 h-16 rounded-full border-4 border-green-500/20 border-t-green-500 animate-spin" style={{ animationDuration: '1s', animationTimingFunction: 'linear' }} />
                        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-xl font-bold text-green-500">Π</span>
                      </div>
                      <h3 className={`text-base sm:text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        PhiCor Deep Analysis
                      </h3>
                      <p className={`text-xs sm:text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {isAnalyzingImages ? 'Analyzing food images with AI vision...' : 'Analyzing nutrients at molecular, cellular & organ levels...'}
                      </p>
                    </div>
                    
                    {/* Did You Know? Fact Card */}
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={currentFactIndex}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5 }}
                        className={`rounded-lg p-4 mb-4 ${isDark ? 'bg-green-900/20 border border-green-500/20' : 'bg-green-50 border border-green-200'}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="text-2xl">{foodFacts[currentFactIndex]?.organ.split(' ')[0]}</div>
                          <div className="flex-1">
                            <p className={`text-xs font-medium mb-1 ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                              💡 Did you know?
                            </p>
                            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              {foodFacts[currentFactIndex]?.fact}
                            </p>
                            <div className="flex gap-2 mt-2">
                              <Badge variant="outline" className={`text-[10px] ${isDark ? 'border-green-500/30 text-green-400' : 'border-green-300 text-green-600'}`}>
                                {foodFacts[currentFactIndex]?.organ}
                              </Badge>
                              <Badge variant="outline" className={`text-[10px] ${isDark ? 'border-purple-500/30 text-purple-400' : 'border-purple-300 text-purple-600'}`}>
                                {foodFacts[currentFactIndex]?.nutrient}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </AnimatePresence>
                    
                    {/* Progress dots */}
                    <div className="flex justify-center gap-1 mb-4">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <div 
                          key={i} 
                          className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            (currentFactIndex % 5) === i 
                              ? 'bg-green-500 w-4' 
                              : isDark ? 'bg-gray-700' : 'bg-gray-300'
                          }`} 
                        />
                      ))}
                    </div>
                    
                    {/* Cancel Button */}
                    <div className="text-center">
                      <p className={`text-[10px] mb-3 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                        ⏱️ Analysis typically takes 5-30 seconds
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={cancelAnalysis}
                        className={`text-xs ${isDark ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-300 hover:bg-gray-100'}`}
                      >
                        <X className="w-3 h-3 mr-1" />
                        Cancel Analysis
                      </Button>
                    </div>
                  </div>
                ) : analysisResult ? (
                  <div className="space-y-4">
                    {/* Header Card with Score Explanation */}
                    <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'}`}>
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 flex-shrink-0 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                          <span className="text-2xl">{getFoodIcon(analysisResult.product_name || analysisResult.dish_name)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {analysisResult.product_name || analysisResult.dish_name}
                          </h2>
                          <p className={`text-xs line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {analysisResult.description}
                          </p>
                          {/* Serving Size - Prominently Displayed */}
                          <div className={`mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${isDark ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                            <Scale className="w-3 h-3" />
                            <span className="font-medium">
                              {analysisResult.serving_display || 
                               (analysisResult.serving_size && analysisResult.serving_size_g 
                                 ? `${analysisResult.serving_size} (${analysisResult.serving_size_g}g)` 
                                 : analysisResult.serving_size || '100g')}
                            </span>
                          </div>
                        </div>
                        {/* Enhanced Score Display */}
                        <div className="text-center flex-shrink-0">
                          {(() => {
                            const score = analysisResult.clinical_summary?.healthfulness_score || analysisResult.healthfulness_score || 50;
                            const scoreInfo = getScoreInfo(score);
                            return (
                              <>
                                <div className={`w-14 h-14 rounded-full flex items-center justify-center border-4 ${
                                  score >= 70 ? 'bg-green-500/20 border-green-500' :
                                  score >= 50 ? 'bg-yellow-500/20 border-yellow-500' : 'bg-orange-500/20 border-orange-500'
                                }`}>
                                  <span className={`text-xl font-bold ${
                                    score >= 70 ? 'text-green-500' :
                                    score >= 50 ? 'text-yellow-500' : 'text-orange-500'
                                  }`}>
                                    {score}
                                  </span>
                                </div>
                                <p className={`text-[10px] mt-1 font-medium ${scoreInfo.color}`}>{scoreInfo.label}</p>
                                <p className={`text-[8px] max-w-[80px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{scoreInfo.desc}</p>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      
                      {/* Export PDF Button */}
                      <div className="flex justify-end mt-3">
                        <PDFExportButton 
                          type="food"
                          data={analysisResult}
                          patientProfile={{
                            height: profile.height,
                            weight: profile.weight,
                            age: profile.age,
                            bmi: profile.weight && profile.height ? (profile.weight / Math.pow(profile.height / 100, 2)) : null
                          }}
                          isDark={isDark}
                          variant="outline"
                          size="sm"
                        />
                      </div>
                    </div>
                    
                    {/* Body Map View */}
                    {showBodyMap && (
                      <div className="space-y-4">
                        <EnhancedBodyMap
                          effects={bodyMapEffects}
                          connections={bodyMapConnections}
                          sourceLabel={analysisResult.foods_analyzed?.[0]?.name || searchQuery}
                          height={450}
                          isDark={isDark}
                          onOrganClick={(organ) => {
                            // Navigate to the organ's detail view
                            const organId = organ.id?.toLowerCase().replace(/\s+/g, '_');
                            if (analysisResult.organ_system_parameter_effects?.[organId]) {
                              setShowBodyMap(false);
                              setActiveScale(organId);
                            }
                          }}
                        />
                        <div className={`text-center text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          Click on organs to see detailed parameter effects
                        </div>
                      </div>
                    )}
                    
                    {/* Overview Tab */}
                    {activeScale === 'overview' && !showBodyMap && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Macros */}
                        <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'}`}>
                          <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Macronutrients
                          </h3>
                          <div className="space-y-2">
                            <NutrientBar label="Calories" value={analysisResult.complete_nutritional_profile?.macronutrients?.energy_kcal || analysisResult.macronutrients?.calories?.value} unit=" kcal" percent={0} color="bg-orange-500" isDark={isDark} />
                            <NutrientBar label="Protein" value={analysisResult.complete_nutritional_profile?.macronutrients?.protein_g || analysisResult.macronutrients?.protein?.value} unit="g" percent={0} color="bg-red-500" isDark={isDark} />
                            <NutrientBar label="Carbs" value={analysisResult.complete_nutritional_profile?.macronutrients?.carbohydrates_g || analysisResult.macronutrients?.carbohydrates?.total?.value} unit="g" percent={0} color="bg-yellow-500" isDark={isDark} />
                            <NutrientBar label="Fat" value={analysisResult.complete_nutritional_profile?.macronutrients?.fat_total_g || analysisResult.macronutrients?.fats?.total?.value} unit="g" percent={0} color="bg-blue-500" isDark={isDark} />
                          </div>
                        </div>
                        
                        {/* Key Insights */}
                        <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'}`}>
                          <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Clinical Summary
                          </h3>
                          {analysisResult.clinical_summary?.key_benefits?.length > 0 && (
                            <div className="mb-3">
                              <span className={`text-[10px] font-semibold text-green-500`}>Benefits</span>
                              {analysisResult.clinical_summary.key_benefits.slice(0, 3).map((b, i) => (
                                <div key={i} className="flex items-start gap-2 mt-1">
                                  <CheckCircle className="w-3 h-3 text-green-500 mt-0.5" />
                                  <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{b}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {analysisResult.clinical_summary?.key_concerns?.length > 0 && (
                            <div>
                              <span className={`text-[10px] font-semibold text-orange-500`}>Concerns</span>
                              {analysisResult.clinical_summary.key_concerns.slice(0, 3).map((c, i) => (
                                <div key={i} className="flex items-start gap-2 mt-1">
                                  <AlertTriangle className="w-3 h-3 text-orange-500 mt-0.5" />
                                  <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{c}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* INGREDIENT BREAKDOWN - For complex dishes/recipes */}
                        {analysisResult.is_recipe && analysisResult.ingredient_breakdown && (
                          <div className={`rounded-xl border p-4 lg:col-span-2 ${isDark ? 'bg-gradient-to-br from-gray-900/80 to-gray-900/50 border-gray-800' : 'bg-gradient-to-br from-blue-50/50 to-white border-gray-200'}`}>
                            <div className="flex items-center justify-between mb-3">
                              <h3 className={`text-sm font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                <UtensilsCrossed className="w-4 h-4 text-blue-500" />
                                Ingredient Breakdown
                              </h3>
                              <Badge className={`text-[9px] ${isDark ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
                                {analysisResult.ingredient_breakdown.total_ingredients} ingredients
                              </Badge>
                            </div>
                            
                            {/* Primary Ingredients */}
                            <div className="mb-4">
                              <div className={`text-[10px] font-semibold uppercase tracking-wide mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                Primary Ingredients
                              </div>
                              <div className="space-y-2">
                                {analysisResult.ingredient_breakdown.primary_ingredients?.map((ing, i) => (
                                  <div key={i} className={`flex items-center justify-between p-2 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-white border border-gray-100'}`}>
                                    <div className="flex items-center gap-2">
                                      <span className="text-lg">
                                        {ing.ingredient_name?.toLowerCase().includes('chicken') ? '🍗' :
                                         ing.ingredient_name?.toLowerCase().includes('rice') ? '🍚' :
                                         ing.ingredient_name?.toLowerCase().includes('tomato') ? '🍅' :
                                         ing.ingredient_name?.toLowerCase().includes('cream') ? '🥛' :
                                         ing.ingredient_name?.toLowerCase().includes('egg') ? '🥚' :
                                         ing.ingredient_name?.toLowerCase().includes('beef') ? '🥩' :
                                         ing.ingredient_name?.toLowerCase().includes('pasta') ? '🍝' :
                                         ing.ingredient_name?.toLowerCase().includes('onion') ? '🧅' :
                                         ing.ingredient_name?.toLowerCase().includes('garlic') ? '🧄' :
                                         ing.ingredient_name?.toLowerCase().includes('spinach') ? '🥬' :
                                         ing.ingredient_name?.toLowerCase().includes('fish') || ing.ingredient_name?.toLowerCase().includes('salmon') ? '🐟' :
                                         ing.ingredient_name?.toLowerCase().includes('cheese') || ing.ingredient_name?.toLowerCase().includes('paneer') ? '🧀' :
                                         '🥗'}
                                      </span>
                                      <div>
                                        <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                          {ing.ingredient_name}
                                        </span>
                                        {ing.matched_food && ing.found_in_db && (
                                          <span className={`text-[9px] block ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                                            ✓ DB Match: {ing.matched_food.slice(0, 30)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <span className={`text-xs font-mono ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {ing.amount_g}g
                                      </span>
                                      {ing.nutrients?.energy?.value > 0 && (
                                        <span className={`text-[9px] block ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                                          {Math.round(ing.nutrients.energy?.value || ing.nutrients.calories?.value || 0)} kcal
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            {/* Secondary Ingredients */}
                            {analysisResult.ingredient_breakdown.secondary_ingredients?.length > 0 && (
                              <div className="mb-3">
                                <Collapsible>
                                  <CollapsibleTrigger className="flex items-center gap-2 w-full">
                                    <div className={`text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                      Secondary Ingredients ({analysisResult.ingredient_breakdown.secondary_ingredients.length})
                                    </div>
                                    <ChevronDown className={`w-3 h-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                                  </CollapsibleTrigger>
                                  <CollapsibleContent className="mt-2">
                                    <div className="grid grid-cols-2 gap-2">
                                      {analysisResult.ingredient_breakdown.secondary_ingredients.map((ing, i) => (
                                        <div key={i} className={`p-2 rounded text-xs ${isDark ? 'bg-gray-800/30' : 'bg-gray-50'}`}>
                                          <span className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{ing.ingredient_name}</span>
                                          <span className={`text-[9px] ml-1 font-mono ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                            ({ing.amount_g}g)
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </CollapsibleContent>
                                </Collapsible>
                              </div>
                            )}
                            
                            {/* Spices & Seasonings */}
                            {analysisResult.ingredient_breakdown.spices_seasonings?.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-dashed border-gray-700/50">
                                <div className={`text-[10px] font-semibold uppercase tracking-wide mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                  🌶️ Spices & Seasonings
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {analysisResult.ingredient_breakdown.spices_seasonings.map((spice, i) => (
                                    <Badge key={i} variant="outline" className={`text-[9px] ${isDark ? 'border-yellow-800/50 text-yellow-400' : 'border-yellow-300 text-yellow-700'}`}>
                                      {spice}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Analysis note */}
                            <div className={`mt-3 pt-3 border-t ${isDark ? 'border-gray-700/50' : 'border-gray-200'}`}>
                              <div className={`text-[9px] flex items-center gap-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                <Microscope className="w-3 h-3" />
                                {analysisResult.ingredient_breakdown.ingredients_found_in_db} of {analysisResult.ingredient_breakdown.total_ingredients} ingredients analyzed from database
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* System Overview - Clickable cards with visual affordance */}
                        {analysisResult.organ_system_parameter_effects && (
                          <div className={`rounded-xl border p-4 lg:col-span-2 ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'}`}>
                            <div className="flex items-center justify-between mb-3">
                              <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                Organ System Effects
                              </h3>
                              <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                👆 Tap for details
                              </span>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              {Object.entries(analysisResult.organ_system_parameter_effects).map(([system, data]) => {
                                // Get a sample quantified change for preview
                                const sampleChange = data.parameters_affected?.find(p => p.quantified_change !== null && p.quantified_change !== undefined);
                                const paramCount = data.parameters_affected?.length || 0;
                                
                                return (
                                  <button
                                    key={system}
                                    onClick={() => setActiveScale(system)}
                                    className={`p-3 rounded-lg border text-center transition-all group relative ${
                                      data.overall_effect === 'beneficial' ? (isDark ? 'bg-green-900/20 border-green-800/30 hover:bg-green-900/40 hover:border-green-600' : 'bg-green-50 border-green-200 hover:bg-green-100 hover:border-green-400') :
                                      data.overall_effect === 'caution' || data.overall_effect === 'harmful' ? (isDark ? 'bg-orange-900/20 border-orange-800/30 hover:bg-orange-900/40 hover:border-orange-600' : 'bg-orange-50 border-orange-200 hover:bg-orange-100 hover:border-orange-400') :
                                      (isDark ? 'bg-gray-800 border-gray-700 hover:bg-gray-700 hover:border-gray-500' : 'bg-gray-100 border-gray-200 hover:bg-gray-200 hover:border-gray-400')
                                    }`}
                                  >
                                    {/* Click indicator */}
                                    <div className={`absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                      <ChevronRight className="w-3 h-3" />
                                    </div>
                                    
                                    <span className="text-xl">
                                      {system === 'cardiovascular' ? '❤️' :
                                       system === 'renal' ? '🫘' :
                                       system === 'hepatic' ? '🫀' :
                                       system === 'glycemic' ? '🍬' :
                                       system === 'gastrointestinal' ? '🥗' :
                                       system === 'neurological' ? '🧠' :
                                       system === 'musculoskeletal' ? '💪' :
                                       system === 'immune' ? '🛡️' : 
                                       system === 'metabolic' ? '🔥' :
                                       system === 'endocrine' ? '⚖️' :
                                       system === 'hematologic' ? '🩸' : '🔬'}
                                    </span>
                                    <div className={`text-[10px] font-medium mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                      {system === 'cardiovascular' ? 'Cardio' :
                                       system === 'gastrointestinal' ? 'GI' :
                                       system === 'musculoskeletal' ? 'MSK' :
                                       system === 'neurological' ? 'Neuro' :
                                       system.charAt(0).toUpperCase() + system.slice(1)}
                                    </div>
                                    
                                    {/* Show sample quantified change if available and non-zero */}
                                    {sampleChange?.quantified_change !== null && sampleChange?.quantified_change !== undefined && sampleChange?.quantified_change !== 0 ? (
                                      <div className={`text-[10px] font-bold mt-1 ${
                                        sampleChange.quantified_change > 0 ? 'text-red-400' : 'text-blue-400'
                                      }`}>
                                        {sampleChange.quantified_change > 0 ? '+' : ''}{sampleChange.quantified_change} {sampleChange.change_unit}
                                      </div>
                                    ) : (
                                      <Badge className={`mt-1 text-[8px] ${
                                        data.overall_effect === 'beneficial' ? 'bg-green-500/30 text-green-400' :
                                        data.overall_effect === 'caution' ? 'bg-orange-500/30 text-orange-400' :
                                        'bg-gray-500/30 text-gray-400'
                                      }`}>
                                        {data.overall_effect}
                                      </Badge>
                                    )}
                                    
                                    {/* Parameter count hint */}
                                    <div className={`text-[9px] mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                      {paramCount} effect{paramCount !== 1 ? 's' : ''}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Individual Food Items Scale - NEW */}
                    {activeScale === 'items' && !showBodyMap && (
                      <div className="space-y-4">
                        {analysisResult.individual_food_items?.length > 0 ? (
                          <>
                            {/* Actionable Insights Summary */}
                            {analysisResult.actionable_insights && (
                              <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'}`}>
                                <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                  <span>💡</span> Quick Actions
                                </h3>
                                <div className="grid gap-3">
                                  {analysisResult.actionable_insights.keep_eating?.length > 0 && (
                                    <div className={`p-3 rounded-lg ${isDark ? 'bg-green-900/20 border border-green-800/30' : 'bg-green-50 border border-green-200'}`}>
                                      <div className="flex items-center gap-2 mb-2">
                                        <CheckCircle className="w-4 h-4 text-green-500" />
                                        <span className={`text-xs font-semibold ${isDark ? 'text-green-400' : 'text-green-700'}`}>Keep Eating</span>
                                      </div>
                                      <ul className="space-y-1">
                                        {analysisResult.actionable_insights.keep_eating.map((item, i) => (
                                          <li key={i} className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>• {item}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {analysisResult.actionable_insights.reduce_or_eliminate?.length > 0 && (
                                    <div className={`p-3 rounded-lg ${isDark ? 'bg-orange-900/20 border border-orange-800/30' : 'bg-orange-50 border border-orange-200'}`}>
                                      <div className="flex items-center gap-2 mb-2">
                                        <AlertTriangle className="w-4 h-4 text-orange-500" />
                                        <span className={`text-xs font-semibold ${isDark ? 'text-orange-400' : 'text-orange-700'}`}>Reduce or Eliminate</span>
                                      </div>
                                      <ul className="space-y-1">
                                        {analysisResult.actionable_insights.reduce_or_eliminate.map((item, i) => (
                                          <li key={i} className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>• {item}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {analysisResult.actionable_insights.add_next_time?.length > 0 && (
                                    <div className={`p-3 rounded-lg ${isDark ? 'bg-blue-900/20 border border-blue-800/30' : 'bg-blue-50 border border-blue-200'}`}>
                                      <div className="flex items-center gap-2 mb-2">
                                        <Plus className="w-4 h-4 text-blue-500" />
                                        <span className={`text-xs font-semibold ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>Add Next Time</span>
                                      </div>
                                      <ul className="space-y-1">
                                        {analysisResult.actionable_insights.add_next_time.map((item, i) => (
                                          <li key={i} className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>• {item}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Individual Food Items List */}
                            <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'}`}>
                              <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                <span>🍽️</span> Individual Food Items ({analysisResult.individual_food_items.length})
                              </h3>
                              <div className="space-y-3">
                                {analysisResult.individual_food_items.map((item, idx) => (
                                  <FoodItemCard key={idx} item={item} isDark={isDark} />
                                ))}
                              </div>
                            </div>
                            
                            {/* Combined Meal Effect */}
                            {analysisResult.combined_meal_analysis && (
                              <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'}`}>
                                <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                  <span>🍱</span> Combined Meal Effect
                                </h3>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                                  <div className={`p-2 rounded-lg text-center ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                                    <div className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{analysisResult.combined_meal_analysis.total_calories}</div>
                                    <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Total kcal</div>
                                  </div>
                                  <div className={`p-2 rounded-lg text-center ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                                    <div className={`text-lg font-bold text-red-400`}>{analysisResult.combined_meal_analysis.total_macros?.protein_g}g</div>
                                    <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Protein</div>
                                  </div>
                                  <div className={`p-2 rounded-lg text-center ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                                    <div className={`text-lg font-bold text-yellow-400`}>{analysisResult.combined_meal_analysis.total_macros?.carbs_g}g</div>
                                    <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Carbs</div>
                                  </div>
                                  <div className={`p-2 rounded-lg text-center ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                                    <div className={`text-lg font-bold text-blue-400`}>{analysisResult.combined_meal_analysis.total_macros?.fat_g}g</div>
                                    <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Fat</div>
                                  </div>
                                </div>
                                
                                {/* Organ System Scores */}
                                {analysisResult.combined_meal_analysis.overall_effect && (
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    {Object.entries(analysisResult.combined_meal_analysis.overall_effect).map(([system, data]) => (
                                      <div key={system} className={`p-2 rounded-lg text-center ${
                                        data.effect === 'beneficial' ? (isDark ? 'bg-green-900/20' : 'bg-green-50') :
                                        data.effect === 'caution' ? (isDark ? 'bg-orange-900/20' : 'bg-orange-50') :
                                        (isDark ? 'bg-gray-800' : 'bg-gray-100')
                                      }`}>
                                        <div className={`text-sm font-bold ${
                                          data.effect === 'beneficial' ? 'text-green-500' :
                                          data.effect === 'caution' ? 'text-orange-500' : 'text-gray-400'
                                        }`}>{data.score || '—'}</div>
                                        <div className={`text-[9px] capitalize ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{system.slice(0, 6)}</div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {/* Synergies & Conflicts */}
                                {(analysisResult.combined_meal_analysis.synergies?.length > 0 || analysisResult.combined_meal_analysis.conflicts?.length > 0) && (
                                  <div className="mt-3 space-y-2">
                                    {analysisResult.combined_meal_analysis.synergies?.map((s, i) => (
                                      <div key={i} className={`text-xs flex items-start gap-2 ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                                        <span>✓</span> {s}
                                      </div>
                                    ))}
                                    {analysisResult.combined_meal_analysis.conflicts?.map((c, i) => (
                                      <div key={i} className={`text-xs flex items-start gap-2 ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                                        <span>⚠</span> {c}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className={`rounded-xl border p-8 text-center ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'}`}>
                            <div className="text-4xl mb-3">📷</div>
                            <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              No Individual Items Data
                            </h3>
                            <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                              Upload food photos to get individual item breakdown with per-food analysis
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Nutrients Scale - Detailed breakdown */}
                    {activeScale === 'nutrients' && !showBodyMap && (
                      <div className="space-y-4">
                        {/* Macronutrients */}
                        <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'}`}>
                          <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            <span>🍽️</span> Macronutrients
                          </h3>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {[
                              { name: 'Energy', value: analysisResult.complete_nutritional_profile?.macronutrients?.energy_kcal, unit: 'kcal', icon: '⚡' },
                              { name: 'Protein', value: analysisResult.complete_nutritional_profile?.macronutrients?.protein_g, unit: 'g', icon: '💪' },
                              { name: 'Carbs', value: analysisResult.complete_nutritional_profile?.macronutrients?.carbohydrates_g, unit: 'g', icon: '🌾' },
                              { name: 'Fat', value: analysisResult.complete_nutritional_profile?.macronutrients?.fat_total_g, unit: 'g', icon: '🥑' },
                              { name: 'Fiber', value: analysisResult.complete_nutritional_profile?.macronutrients?.fiber_g, unit: 'g', icon: '🥬' },
                              { name: 'Sugars', value: analysisResult.complete_nutritional_profile?.macronutrients?.sugars_g, unit: 'g', icon: '🍬' },
                              { name: 'Sat Fat', value: analysisResult.complete_nutritional_profile?.macronutrients?.fat_saturated_g, unit: 'g', icon: '🧈' },
                              { name: 'Cholesterol', value: analysisResult.complete_nutritional_profile?.macronutrients?.cholesterol_mg, unit: 'mg', icon: '❤️' },
                            ].map((n, i) => (
                              <div key={i} className={`p-2 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                                <div className="flex items-center gap-1 mb-1">
                                  <span className="text-sm">{n.icon}</span>
                                  <span className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{n.name}</span>
                                </div>
                                <div className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                  {n.value ?? '—'} {n.value && n.unit}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Electrolytes */}
                        {analysisResult.complete_nutritional_profile?.electrolytes && (
                          <div className={`rounded-xl border p-4 ${isDark ? 'bg-blue-900/20 border-blue-800/30' : 'bg-blue-50 border-blue-200'}`}>
                            <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
                              <span>⚡</span> Electrolytes
                            </h3>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {Object.entries(analysisResult.complete_nutritional_profile.electrolytes).map(([key, data]) => (
                                <div key={key} className={`p-2 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                                  <div className={`text-[10px] capitalize ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {key.replace(/_/g, ' ')}
                                  </div>
                                  <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {typeof data === 'object' ? data.value : data} mg
                                  </div>
                                  {typeof data === 'object' && data.percent_daily && (
                                    <div className="text-[9px] text-blue-500">{data.percent_daily}% DV</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Vitamins */}
                        {analysisResult.complete_nutritional_profile?.vitamins && (
                          <div className={`rounded-xl border p-4 ${isDark ? 'bg-yellow-900/20 border-yellow-800/30' : 'bg-yellow-50 border-yellow-200'}`}>
                            <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-yellow-400' : 'text-yellow-700'}`}>
                              <span>💊</span> Vitamins
                            </h3>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              {Object.entries(analysisResult.complete_nutritional_profile.vitamins).map(([key, data]) => (
                                <div key={key} className={`p-2 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                                  <div className={`text-[9px] uppercase ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {key.replace(/_/g, ' ').replace('vitamin ', 'Vit ')}
                                  </div>
                                  <div className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {typeof data === 'object' ? data.value : data}
                                  </div>
                                  {typeof data === 'object' && data.percent_daily && (
                                    <div className="text-[9px] text-yellow-500">{data.percent_daily}%</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Trace Elements */}
                        {analysisResult.complete_nutritional_profile?.trace_elements && (
                          <div className={`rounded-xl border p-4 ${isDark ? 'bg-purple-900/20 border-purple-800/30' : 'bg-purple-50 border-purple-200'}`}>
                            <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-purple-400' : 'text-purple-700'}`}>
                              <span>🔬</span> Trace Elements
                            </h3>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              {Object.entries(analysisResult.complete_nutritional_profile.trace_elements).map(([key, data]) => (
                                <div key={key} className={`p-2 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                                  <div className={`text-[9px] capitalize ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {key.replace(/_/g, ' ')}
                                  </div>
                                  <div className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {typeof data === 'object' ? data.value : data}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Molecular Scale - Bioactive compounds */}
                    {activeScale === 'molecular' && !showBodyMap && (
                      <div className="space-y-4">
                        {/* For image analysis - show food composition breakdown */}
                        {analysisResult.individual_food_items?.length > 0 && (
                          <div className={`rounded-xl border p-4 ${isDark ? 'bg-indigo-900/20 border-indigo-800/30' : 'bg-indigo-50 border-indigo-200'}`}>
                            <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-indigo-400' : 'text-indigo-700'}`}>
                              <span>🧬</span> Molecular Composition of Meal
                            </h3>
                            <p className={`text-xs mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              Key molecular components identified in this meal
                            </p>
                            <div className="grid gap-2">
                              {analysisResult.individual_food_items.map((item, idx) => (
                                <div key={idx} className={`p-3 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-lg">{getFoodIcon(item.name)}</span>
                                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.name}</span>
                                  </div>
                                  {/* Key nutrients/molecules */}
                                  <div className="flex flex-wrap gap-1">
                                    {item.key_nutrients?.slice(0, 5).map((n, i) => (
                                      <Badge key={i} variant="outline" className="text-[9px]">{n}</Badge>
                                    ))}
                                    {!item.key_nutrients && item.macros && (
                                      <>
                                        <Badge variant="outline" className="text-[9px]">Protein: {item.macros.protein}g</Badge>
                                        <Badge variant="outline" className="text-[9px]">Carbs: {item.macros.carbs}g</Badge>
                                        <Badge variant="outline" className="text-[9px]">Fat: {item.macros.fat}g</Badge>
                                      </>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Bioactive Compounds from ingredients */}
                        {analysisResult.ingredients_breakdown?.primary_ingredients?.some(i => i.bioactive_compounds?.length > 0) && (
                          <div className={`rounded-xl border p-4 ${isDark ? 'bg-cyan-900/20 border-cyan-800/30' : 'bg-cyan-50 border-cyan-200'}`}>
                            <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-cyan-400' : 'text-cyan-700'}`}>
                              <span>⚗️</span> Bioactive Compounds
                            </h3>
                            <div className="flex flex-wrap gap-2">
                              {analysisResult.ingredients_breakdown.primary_ingredients.flatMap(i => i.bioactive_compounds || []).map((c, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{c}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Other Compounds */}
                        {analysisResult.complete_nutritional_profile?.other_compounds && (
                          <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'}`}>
                            <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              <span>🧪</span> Other Compounds
                            </h3>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {Object.entries(analysisResult.complete_nutritional_profile.other_compounds).filter(([k, v]) => v && v > 0).map(([key, value]) => (
                                <div key={key} className={`p-2 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                                  <div className={`text-[10px] capitalize ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {key.replace(/_/g, ' ')}
                                  </div>
                                  <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {value} mg
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Additives */}
                        {analysisResult.ingredients_breakdown?.additives_and_preservatives?.length > 0 && (
                          <div className={`rounded-xl border p-4 ${isDark ? 'bg-orange-900/20 border-orange-800/30' : 'bg-orange-50 border-orange-200'}`}>
                            <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-orange-400' : 'text-orange-700'}`}>
                              <span>⚠️</span> Additives & Preservatives
                            </h3>
                            <div className="space-y-2">
                              {analysisResult.ingredients_breakdown.additives_and_preservatives.map((a, i) => (
                                <div key={i} className={`p-2 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                                  <div className="flex items-center gap-2">
                                    <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{a.name}</span>
                                    {a.e_number && <Badge variant="outline" className="text-[9px]">{a.e_number}</Badge>}
                                  </div>
                                  <div className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{a.function}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Fallback: if no molecular data available */}
                        {!analysisResult.individual_food_items?.length && 
                         !analysisResult.ingredients_breakdown?.primary_ingredients?.length && 
                         !analysisResult.complete_nutritional_profile?.other_compounds && (
                          <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
                            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              Molecular composition data not available for this analysis. Try searching for a specific food or uploading a meal image.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Cellular Scale */}
                    {activeScale === 'cellular' && !showBodyMap && (
                      <div className="space-y-4">
                        {/* For image analysis with identified food items - show per-item cellular effects */}
                        {analysisResult.individual_food_items?.length > 0 && (
                          <div className={`rounded-xl border p-4 ${isDark ? 'bg-purple-900/20 border-purple-800/30' : 'bg-purple-50 border-purple-200'}`}>
                            <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-purple-400' : 'text-purple-700'}`}>
                              <span>🍽️</span> Food Items in This Meal ({analysisResult.individual_food_items.length})
                            </h3>
                            <div className="grid gap-2">
                              {analysisResult.individual_food_items.map((item, idx) => (
                                <div key={idx} className={`p-3 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-xl">{getFoodIcon(item.name)}</span>
                                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.name}</span>
                                    {item.estimated_weight && (
                                      <Badge variant="outline" className="ml-auto text-[10px]">{item.estimated_weight}</Badge>
                                    )}
                                  </div>
                                  <div className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {item.calories && `${item.calories} kcal`}
                                    {item.macros && ` • P: ${item.macros.protein || '?'}g • C: ${item.macros.carbs || '?'}g • F: ${item.macros.fat || '?'}g`}
                                  </div>
                                  {/* Show per-item cellular effects */}
                                  {item.health_effects && (
                                    <div className="mt-2 flex flex-wrap gap-1">
                                      {item.health_effects.benefits?.slice(0, 2).map((b, i) => (
                                        <Badge key={`b-${i}`} className="text-[8px] bg-green-500/20 text-green-400">{b}</Badge>
                                      ))}
                                      {item.health_effects.concerns?.slice(0, 2).map((c, i) => (
                                        <Badge key={`c-${i}`} className="text-[8px] bg-orange-500/20 text-orange-400">{c}</Badge>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Combined Cellular Effects across organ systems */}
                        <div className={`rounded-xl border p-4 ${isDark ? 'bg-green-900/20 border-green-800/30' : 'bg-green-50 border-green-200'}`}>
                          <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                            <span>🔬</span> {analysisResult.individual_food_items?.length > 0 ? 'Combined Cellular Effects' : 'Cellular Effects'}
                          </h3>
                          <p className={`text-xs mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {analysisResult.individual_food_items?.length > 0 
                              ? 'How this meal affects cellular processes across organ systems'
                              : 'How nutrients affect cellular processes'}
                          </p>
                          <div className="grid gap-2">
                            {ORGAN_SYSTEMS.map(sys => {
                              const sysData = analysisResult.organ_system_parameter_effects?.[sys.id];
                              if (!sysData) return null;
                              return (
                                <div key={sys.id} className={`p-3 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                                  <div className="flex items-center gap-2 mb-2">
                                    <span>{sys.icon}</span>
                                    <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{sys.name}</span>
                                    <Badge className={`ml-auto text-[8px] ${
                                      sysData.overall_effect === 'beneficial' ? 'bg-green-500/30 text-green-400' :
                                      sysData.overall_effect === 'caution' ? 'bg-orange-500/30 text-orange-400' :
                                      'bg-gray-500/30 text-gray-400'
                                    }`}>
                                      {sysData.overall_effect}
                                    </Badge>
                                  </div>
                                  {sysData.parameters_affected?.slice(0, 2).map((p, i) => (
                                    <div key={i} className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                      • {p.parameter}: {p.quantified_change || p.direction || ''} {p.mechanism && `(${p.mechanism})`}
                                    </div>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Physiology Scale - Organ System Detail */}
                    {activeScale === 'physiology' && !showBodyMap && (
                      <div className="space-y-4">
                        {ORGAN_SYSTEMS.map(sys => {
                          const sysData = analysisResult.organ_system_parameter_effects?.[sys.id];
                          if (!sysData?.parameters_affected?.length) return null;
                          return (
                            <div key={sys.id} className={`rounded-xl border p-4 ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'}`}>
                              <div className="flex items-center gap-2 mb-3">
                                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${sys.color} flex items-center justify-center`}>
                                  <span className="text-lg">{sys.icon}</span>
                                </div>
                                <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                  {sys.name} System
                                </h3>
                                <Badge className={`ml-auto ${
                                  sysData.overall_effect === 'beneficial' ? 'bg-green-500/20 text-green-400' :
                                  sysData.overall_effect === 'caution' ? 'bg-orange-500/20 text-orange-400' :
                                  'bg-gray-500/20 text-gray-400'
                                }`}>
                                  {sysData.overall_effect}
                                </Badge>
                              </div>
                              <div className="grid gap-2">
                                {sysData.parameters_affected.map((param, i) => (
                                  <ParameterCard key={i} param={param} isDark={isDark} />
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Individual Organ System View - when clicked from overview */}
                    {analysisResult.organ_system_parameter_effects?.[activeScale] && !showBodyMap && !['overview', 'nutrients', 'molecular', 'cellular', 'physiology', 'clinical'].includes(activeScale) && (
                      <div 
                        className="space-y-4"
                        {...swipeHandlers}
                      >
                        {(() => {
                          const sysData = analysisResult.organ_system_parameter_effects[activeScale];
                          const sysInfo = ORGAN_SYSTEMS.find(s => s.id === activeScale) || { name: activeScale, icon: '🔬', color: 'from-gray-500 to-gray-600' };
                          
                          // Get list of all organ systems for navigation
                          const allSystems = Object.keys(analysisResult.organ_system_parameter_effects);
                          const currentIndex = allSystems.indexOf(activeScale);
                          const prevSystem = currentIndex > 0 ? allSystems[currentIndex - 1] : null;
                          const nextSystem = currentIndex < allSystems.length - 1 ? allSystems[currentIndex + 1] : null;
                          
                          return (
                            <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'}`}>
                              {/* Prominent Back Button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setActiveScale('overview')}
                                className={`mb-4 ${isDark ? 'border-gray-700 hover:bg-gray-800' : ''}`}
                              >
                                <ArrowLeft className="w-4 h-4 mr-1" />
                                Back to Overview
                              </Button>
                              
                              <div className="flex items-center gap-3 mb-4">
                                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${sysInfo.color} flex items-center justify-center`}>
                                  <span className="text-2xl">{sysInfo.icon}</span>
                                </div>
                                <div className="flex-1">
                                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {sysInfo.name} System Effects
                                  </h3>
                                  <Badge className={`mt-1 ${
                                    sysData.overall_effect === 'beneficial' ? 'bg-green-500/20 text-green-400' :
                                    sysData.overall_effect === 'caution' ? 'bg-orange-500/20 text-orange-400' :
                                    'bg-gray-500/20 text-gray-400'
                                  }`}>
                                    {sysData.overall_effect}
                                  </Badge>
                                </div>
                              </div>
                              
                              {/* Swipe Navigation Hint + Buttons */}
                              <div className={`flex items-center justify-between mb-4 ${isDark ? 'bg-gray-800/50' : 'bg-gray-100'} rounded-lg p-2`}>
                                {prevSystem ? (
                                  <button 
                                    onClick={() => setActiveScale(prevSystem)}
                                    className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-200 text-gray-600'}`}
                                  >
                                    <ChevronLeft className="w-4 h-4" />
                                    <span className="hidden sm:inline">{prevSystem.charAt(0).toUpperCase() + prevSystem.slice(1)}</span>
                                  </button>
                                ) : (
                                  <button 
                                    onClick={() => setActiveScale('overview')}
                                    className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-200 text-gray-600'}`}
                                  >
                                    <ChevronLeft className="w-4 h-4" />
                                    <span className="hidden sm:inline">Overview</span>
                                  </button>
                                )}
                                <div className="flex flex-col items-center">
                                  <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>
                                    {currentIndex + 1} of {allSystems.length}
                                  </span>
                                  <span className={`text-[9px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                    👆 Swipe to navigate
                                  </span>
                                </div>
                                {nextSystem ? (
                                  <button 
                                    onClick={() => setActiveScale(nextSystem)}
                                    className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-200 text-gray-600'}`}
                                  >
                                    <span className="hidden sm:inline">{nextSystem.charAt(0).toUpperCase() + nextSystem.slice(1)}</span>
                                    <ChevronRight className="w-4 h-4" />
                                  </button>
                                ) : <span className="w-16" />}
                              </div>
                              
                              {sysData.contributing_nutrients?.length > 0 && (
                                <div className="mb-4">
                                  <span className={`text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                    Contributing Nutrients
                                  </span>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {sysData.contributing_nutrients.map((n, i) => (
                                      <Badge key={i} variant="outline" className="text-[9px]">{n}</Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <div className="grid gap-2">
                                {sysData.parameters_affected?.map((param, i) => (
                                  <ParameterCard key={i} param={param} isDark={isDark} />
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    
                    {/* Clinical Scale - Lab parameters */}
                    {activeScale === 'clinical' && (
                      <div className="space-y-4">
                        <div className={`rounded-xl border p-4 ${isDark ? 'bg-red-900/20 border-red-800/30' : 'bg-red-50 border-red-200'}`}>
                          <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-red-400' : 'text-red-700'}`}>
                            <span>🩺</span> Clinical Parameters Affected
                          </h3>
                          <div className="grid gap-2">
                            {Object.entries(analysisResult.organ_system_parameter_effects || {}).flatMap(([sys, data]) => 
                              (data.parameters_affected || []).filter(p => 
                                ['GFR', 'creatinine', 'ALT', 'AST', 'cholesterol', 'glucose', 'hemoglobin', 'potassium', 'sodium'].some(k => 
                                  p.parameter?.toLowerCase().includes(k.toLowerCase())
                                )
                              ).map((p, i) => (
                                <div key={`${sys}-${i}`} className={`p-2 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                                  <div className="flex items-center justify-between">
                                    <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                      {p.parameter?.replace(/_/g, ' ')}
                                    </span>
                                    <Badge className={`text-[9px] ${
                                      p.direction === 'increase' ? 'bg-red-500/20 text-red-400' :
                                      p.direction === 'decrease' ? 'bg-blue-500/20 text-blue-400' :
                                      'bg-gray-500/20 text-gray-400'
                                    }`}>
                                      {p.direction === 'increase' ? '↑' : p.direction === 'decrease' ? '↓' : '—'} {p.magnitude || ''}
                                    </Badge>
                                  </div>
                                  <div className={`text-[10px] mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {p.mechanism}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                        
                        {/* Drug Interactions */}
                        {analysisResult.personalized_risk_assessment?.medication_interactions?.length > 0 && (
                          <div className={`rounded-xl border p-4 ${isDark ? 'bg-purple-900/20 border-purple-800/30' : 'bg-purple-50 border-purple-200'}`}>
                            <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-purple-400' : 'text-purple-700'}`}>
                              <span>💊</span> Drug Interactions
                            </h3>
                            <div className="space-y-2">
                              {analysisResult.personalized_risk_assessment.medication_interactions.map((int, i) => (
                                <div key={i} className={`p-2 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                                  <div className="flex items-center gap-2">
                                    <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{int.medication}</span>
                                    <Badge className={`text-[9px] ${
                                      int.severity === 'major' ? 'bg-red-500/20 text-red-400' :
                                      int.severity === 'moderate' ? 'bg-orange-500/20 text-orange-400' :
                                      'bg-yellow-500/20 text-yellow-400'
                                    }`}>
                                      {int.severity}
                                    </Badge>
                                  </div>
                                  <div className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {int.interacting_component}: {int.interaction_type}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Personalized Risk Assessment */}
                    {analysisResult.personalized_risk_assessment && (
                      <div className={`rounded-xl border p-4 ${isDark ? 'bg-orange-900/20 border-orange-800/30' : 'bg-orange-50 border-orange-200'}`}>
                        <h3 className={`text-sm font-semibold flex items-center gap-2 mb-3 ${isDark ? 'text-orange-400' : 'text-orange-700'}`}>
                          <AlertTriangle className="w-4 h-4" />
                          Personalized Risk Assessment
                        </h3>
                        <div className="flex items-center gap-2 mb-3">
                          <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Suitability:</span>
                          <Badge className={`${
                            analysisResult.personalized_risk_assessment.overall_suitability === 'highly_suitable' ? 'bg-green-500' :
                            analysisResult.personalized_risk_assessment.overall_suitability === 'suitable' ? 'bg-emerald-500' :
                            analysisResult.personalized_risk_assessment.overall_suitability === 'caution' ? 'bg-yellow-500' : 'bg-red-500'
                          } text-white`}>
                            {analysisResult.personalized_risk_assessment.overall_suitability?.replace(/_/g, ' ')}
                          </Badge>
                        </div>
                        {analysisResult.personalized_risk_assessment.condition_specific_analysis?.length > 0 && (
                          <div className="space-y-2">
                            {analysisResult.personalized_risk_assessment.condition_specific_analysis.map((ca, i) => (
                              <div key={i} className={`p-2 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                                <div className="flex items-center justify-between">
                                  <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{ca.condition}</span>
                                  <Badge className={`text-[9px] ${ca.risk_level === 'high' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                    {ca.risk_level} risk
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  /* Empty State with Popular Foods */
                  <div className="space-y-6">
                    {/* Hero Section */}
                    <div className={`rounded-xl border p-6 text-center ${isDark ? 'bg-gradient-to-br from-gray-900 to-gray-800 border-gray-700' : 'bg-gradient-to-br from-white to-gray-50 border-gray-200'}`}>
                      <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-4 shadow-lg">
                        <span className="text-4xl font-bold text-white">Π</span>
                      </div>
                      <h2 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        PhiCor Food Analysis
                      </h2>
                      <p className={`text-sm mb-4 max-w-md mx-auto ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Discover how any food affects your body at the molecular, cellular, and organ level. 
                        Search any food or pick from popular choices below.
                      </p>
                      
                      {/* Quick Search Suggestions */}
                      <div className="flex flex-wrap gap-2 justify-center">
                        {QUICK_FOODS.slice(0, 6).map(food => (
                          <Button 
                            key={food} 
                            variant="outline" 
                            size="sm" 
                            onClick={() => analyzeFood(food)} 
                            className={`text-xs ${isDark ? 'border-gray-700 hover:bg-gray-800' : ''}`}
                          >
                            {getFoodIcon(food)} {food}
                          </Button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Popular Foods by Category */}
                    <div>
                      <h3 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        <Sparkles className="w-4 h-4 text-yellow-500" />
                        Popular Foods by Health Goal
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {POPULAR_FOODS_BY_CATEGORY.map((category, idx) => (
                          <div 
                            key={category.category}
                            className={`rounded-xl border p-4 transition-all hover:shadow-md ${
                              isDark ? `${category.bgDark} border-gray-700 hover:border-gray-600` : `${category.bgLight} border-gray-200 hover:border-gray-300`
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-3">
                              <span className="text-xl">{category.icon}</span>
                              <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {category.category}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {category.foods.map(food => (
                                <button
                                  key={food}
                                  onClick={() => analyzeFood(food)}
                                  className={`px-2.5 py-1 rounded-full text-xs transition-all hover:scale-105 ${
                                    isDark 
                                      ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                                      : 'bg-white text-gray-700 hover:bg-gray-100 shadow-sm'
                                  }`}
                                >
                                  {getFoodIcon(food)} {food}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Tips Section */}
                    <div className={`rounded-xl border p-4 ${isDark ? 'bg-blue-900/10 border-blue-800/30' : 'bg-blue-50 border-blue-200'}`}>
                      <h4 className={`text-xs font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
                        💡 Pro Tips
                      </h4>
                      <ul className={`text-xs space-y-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <li>• Search for complex dishes like Chicken Tikka Masala - PhiCor breaks them down into ingredients</li>
                        <li>• Try supplements like Vitamin D or Omega-3 to see their organ-level effects</li>
                        <li>• Use the Quantity slider to see how serving size affects nutrient intake</li>
                        <li>• Click the Body Map tab to visualize which organs are affected by each food</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
          
          {/* Food Browser Sidebar - Right Panel */}
          {showFoodBrowser && (
            <div className={`hidden lg:flex flex-col w-80 flex-shrink-0 border-l overflow-hidden ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'}`}>
              <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: isDark ? '#374151' : '#e5e7eb' }}>
                <h3 className={`text-sm font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <Apple className="w-4 h-4 text-green-500" />
                  Food Database
                  {foodBrowserData && (
                    <Badge className="text-[9px] bg-green-500/20 text-green-500 ml-1">
                      {foodBrowserData.total_foods?.toLocaleString()} items
                    </Badge>
                  )}
                </h3>
                <Button variant="ghost" size="sm" onClick={() => setShowFoodBrowser(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Search */}
              <div className="p-3 border-b" style={{ borderColor: isDark ? '#374151' : '#e5e7eb' }}>
                <div className="relative">
                  <Input
                    value={foodBrowserSearch}
                    onChange={(e) => setFoodBrowserSearch(e.target.value)}
                    placeholder="Search foods..."
                    className={`pr-8 text-xs ${isDark ? 'bg-gray-800 border-gray-700' : ''}`}
                  />
                  <Search className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                </div>
                {foodBrowserSearch && filteredFoodBrowserData && (
                  <p className={`text-[10px] mt-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Found {filteredFoodBrowserData.total_foods?.toLocaleString()} matching foods
                  </p>
                )}
              </div>
              
              {/* Food List */}
              <ScrollArea className="flex-1">
                <div className="p-2">
                  {foodBrowserLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-green-500" />
                      <span className={`ml-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Loading foods...</span>
                    </div>
                  ) : filteredFoodBrowserData?.categories ? (
                    Object.entries(filteredFoodBrowserData.categories)
                      .sort((a, b) => b[1].count - a[1].count)
                      .map(([category, data]) => (
                        <Collapsible 
                          key={category} 
                          open={expandedCategories[category]} 
                          onOpenChange={() => toggleCategory(category)}
                          className="mb-1"
                        >
                          <CollapsibleTrigger className={`w-full flex items-center justify-between p-2 rounded-lg text-left transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}>
                            <div className="flex items-center gap-2">
                              <span className="text-base">{data.icon}</span>
                              <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{category}</span>
                              <Badge variant="outline" className="text-[9px] h-4 px-1.5">{data.count}</Badge>
                            </div>
                            {expandedCategories[category] ? (
                              <ChevronUp className="w-3.5 h-3.5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                            )}
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className={`ml-6 mr-1 mt-1 max-h-48 overflow-y-auto rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                              {data.foods.slice(0, 100).map((food, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => handleFoodBrowserSelect(food)}
                                  className={`w-full text-left p-1.5 text-[11px] border-b last:border-0 transition-colors truncate ${
                                    isDark 
                                      ? 'border-gray-700/50 hover:bg-gray-700/50 text-gray-300' 
                                      : 'border-gray-200/50 hover:bg-gray-100 text-gray-700'
                                  }`}
                                  title={food}
                                >
                                  {food}
                                </button>
                              ))}
                              {data.foods.length > 100 && (
                                <div className={`p-2 text-center text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                  + {data.foods.length - 100} more items
                                </div>
                              )}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      ))
                  ) : (
                    <div className={`text-center py-8 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      No foods found
                    </div>
                  )}
                </div>
              </ScrollArea>
              
              {/* Footer Stats */}
              <div className={`p-3 border-t text-center ${isDark ? 'bg-gray-900/80 border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
                <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Click any food to analyze its effects on your body
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Mobile Panel */}
        <AnimatePresence>
          {showPanel && (
            <>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setShowPanel(false)} />
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                className={`fixed left-0 top-0 h-full w-[85%] max-w-sm z-50 overflow-y-auto ${isDark ? 'bg-gray-900' : 'bg-white'}`}
              >
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Subject Profile</h3>
                    <Button variant="ghost" size="sm" onClick={() => setShowPanel(false)}><X className="w-4 h-4" /></Button>
                  </div>
                  
                  {/* Same tabs as desktop */}
                  <Tabs value={panelTab} onValueChange={setPanelTab}>
                    <TabsList className="w-full grid grid-cols-4 h-8 mb-3">
                      <TabsTrigger value="demographics" className="text-[9px]">Bio</TabsTrigger>
                      <TabsTrigger value="medical" className="text-[9px]">Medical</TabsTrigger>
                      <TabsTrigger value="dietary" className="text-[9px]">Diet</TabsTrigger>
                      <TabsTrigger value="intake" className="text-[9px]">Intake</TabsTrigger>
                    </TabsList>
                    
                    {/* Demographics */}
                    <TabsContent value="demographics" className="space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Age</label>
                          <Input type="number" value={profile.age} onChange={(e) => setProfile(p => ({ ...p, age: e.target.value }))} placeholder="Years" className={`h-8 text-xs ${isDark ? 'bg-gray-800 border-gray-700' : ''}`} />
                        </div>
                        <div>
                          <label className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Sex</label>
                          <select value={profile.sex} onChange={(e) => setProfile(p => ({ ...p, sex: e.target.value }))} className={`w-full h-8 text-xs rounded-md border ${isDark ? 'bg-gray-800 border-gray-700' : ''}`}>
                            <option value="">Select</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                          </select>
                        </div>
                        <div>
                          <label className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Weight (kg)</label>
                          <Input type="number" value={profile.weight} onChange={(e) => setProfile(p => ({ ...p, weight: e.target.value }))} placeholder="kg" className={`h-8 text-xs ${isDark ? 'bg-gray-800 border-gray-700' : ''}`} />
                        </div>
                        <div>
                          <label className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Height (cm)</label>
                          <Input type="number" value={profile.height} onChange={(e) => setProfile(p => ({ ...p, height: e.target.value }))} placeholder="cm" className={`h-8 text-xs ${isDark ? 'bg-gray-800 border-gray-700' : ''}`} />
                        </div>
                      </div>
                      {bmi && (
                        <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-green-50'}`}>
                          <div className="flex justify-between"><span className="text-xs">BMI</span><span className="text-lg font-bold">{bmi}</span></div>
                        </div>
                      )}
                    </TabsContent>
                    
                    {/* Medical */}
                    <TabsContent value="medical" className="space-y-3">
                      <div>
                        <label className={`text-[10px] font-semibold ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Medical Conditions</label>
                        <Input value={diseaseSearch} onChange={(e) => setDiseaseSearch(e.target.value)} placeholder="Search diseases..." className={`h-8 text-xs mt-1 ${isDark ? 'bg-gray-800 border-gray-700' : ''}`} />
                        <div className="flex flex-wrap gap-1 mt-2 max-h-32 overflow-y-auto">
                          {filteredDiseases.map(d => (
                            <button key={d} onClick={() => { setProfile(p => ({ ...p, conditions: [...p.conditions, d] })); setDiseaseSearch(''); }}
                              className={`px-2 py-1 rounded text-[9px] border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'}`}>
                              + {d}
                            </button>
                          ))}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {profile.conditions.map((c, i) => (
                            <Badge key={i} className="text-[9px] bg-blue-500/20 text-blue-400 cursor-pointer"
                              onClick={() => setProfile(p => ({ ...p, conditions: p.conditions.filter((_, idx) => idx !== i) }))}>
                              {c} <X className="w-2 h-2 ml-1" />
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className={`text-[10px] font-semibold ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Allergies</label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {COMMON_ALLERGIES.map(a => (
                            <button key={a} onClick={() => setProfile(p => ({ ...p, allergies: p.allergies.includes(a) ? p.allergies.filter(x => x !== a) : [...p.allergies, a] }))}
                              className={`px-2 py-1 rounded text-[9px] border ${profile.allergies.includes(a) ? 'bg-red-500/20 border-red-500/50 text-red-400' : isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'}`}>
                              {a}
                            </button>
                          ))}
                        </div>
                      </div>
                    </TabsContent>
                    
                    {/* Dietary */}
                    <TabsContent value="dietary" className="space-y-3">
                      <div className="grid grid-cols-2 gap-1">
                        {DIETARY_PREFS.map(pref => (
                          <button key={pref.id} onClick={() => setProfile(p => ({ ...p, dietaryPrefs: p.dietaryPrefs.includes(pref.id) ? p.dietaryPrefs.filter(x => x !== pref.id) : [...p.dietaryPrefs, pref.id] }))}
                            className={`p-2 rounded text-[10px] flex items-center gap-1 border ${profile.dietaryPrefs.includes(pref.id) ? 'bg-green-500/20 border-green-500/50 text-green-400' : isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'}`}>
                            <span>{pref.icon}</span><span>{pref.label}</span>
                          </button>
                        ))}
                      </div>
                    </TabsContent>
                    
                    {/* Intake */}
                    <TabsContent value="intake" className="space-y-3">
                      <Textarea value={profile.foodIntake} onChange={(e) => setProfile(p => ({ ...p, foodIntake: e.target.value }))}
                        placeholder="What did you eat today?&#10;Breakfast: ...&#10;Lunch: ...&#10;Dinner: ..."
                        className={`min-h-[100px] text-xs ${isDark ? 'bg-gray-800 border-gray-700' : ''}`} />
                      <Button onClick={analyzeIntake} disabled={isAnalyzing || !profile.foodIntake.trim()} className="w-full bg-gradient-to-r from-green-500 to-emerald-500">
                        {isAnalyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                        Analyze Text
                      </Button>
                      
                      {/* Mobile Image Upload */}
                      <div className="flex items-center gap-2 py-1">
                        <div className={`flex-1 h-px ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                        <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>OR</span>
                        <div className={`flex-1 h-px ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                      </div>
                      
                      {/* Hidden file input for mobile - using label for better compatibility */}
                      <input
                        type="file"
                        ref={mobileFileInputRef}
                        onChange={handleImageUpload}
                        accept="image/*"
                        multiple
                        className="hidden"
                        id="mobile-food-upload"
                      />
                      
                      <label
                        htmlFor="mobile-food-upload"
                        className={`w-full h-16 border-dashed border-2 rounded-md flex flex-col gap-1 items-center justify-center cursor-pointer transition-colors ${
                          isDark 
                            ? 'border-gray-700 hover:bg-gray-800 hover:border-gray-600' 
                            : 'border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                        } ${foodImages.length >= 10 ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Camera className="w-5 h-5 text-purple-500" />
                        <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          Upload Photos ({foodImages.length}/10)
                        </span>
                      </label>
                      
                      {foodImages.length > 0 && (
                        <>
                          <div className="grid grid-cols-5 gap-1">
                            {foodImages.map((img) => (
                              <div key={img.id} className="relative group aspect-square">
                                <img src={img.preview} alt={img.name} className="w-full h-full object-cover rounded" />
                                <button onClick={() => removeImage(img.id)} className="absolute top-0 right-0 p-0.5 bg-red-500 rounded-bl text-white">
                                  <X className="w-2 h-2" />
                                </button>
                              </div>
                            ))}
                          </div>
                          <Button onClick={analyzeImages} disabled={isAnalyzingImages} className="w-full bg-gradient-to-r from-purple-500 to-pink-500">
                            {isAnalyzingImages ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Image className="w-4 h-4 mr-2" />}
                            Analyze Photos
                          </Button>
                        </>
                      )}
                    </TabsContent>
                  </Tabs>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default FoodAnalysisPage;
